/**
 * Every word the staff portal shows.
 *
 * Russian first because that is what the counter reads; English is kept so the
 * owner and anyone reading the code can follow the same screen.
 *
 * Keys are grouped by where they appear. Anything that comes from the server
 * and is not already translated there — permission descriptions, role names,
 * setting labels — is translated here too, under `perm.`, `role.` and
 * `setting.`, so the screen never shows a raw English string from the database.
 */
export const STRINGS = {
  // ---- navigation -----------------------------------------------------------
  'nav.dashboard': { ru: 'Сводка', en: 'Dashboard' },
  'nav.customers': { ru: 'Клиенты', en: 'Customers' },
  'nav.newOrder': { ru: 'Новый заказ', en: 'New order' },
  'nav.scan': { ru: 'Сканировать', en: 'Scan QR' },
  'nav.orders': { ru: 'Заказы', en: 'Orders' },
  'nav.followups': { ru: 'Напоминания', en: 'Follow-ups' },
  'nav.floor': { ru: 'На площадке', en: 'On the floor' },
  'nav.myWork': { ru: 'Моя работа', en: 'My work' },
  'nav.vehicles': { ru: 'Автопарк', en: 'Vehicles' },
  'nav.services': { ru: 'Услуги', en: 'Services' },
  'nav.rewards': { ru: 'Награды', en: 'Rewards' },
  'nav.wallets': { ru: 'Карты в телефоне', en: 'Wallets' },
  'nav.imports': { ru: 'Импорт', en: 'Import' },
  'nav.reports': { ru: 'Отчёты', en: 'Reports' },
  'nav.payroll': { ru: 'Зарплаты', en: 'Payroll' },
  'payroll.title': { ru: 'Зарплаты', en: 'Payroll' },
  'payroll.today': { ru: 'Сегодня', en: 'Today' },
  'payroll.week': { ru: 'Неделя', en: 'Week' },
  'payroll.month': { ru: 'Месяц', en: 'Month' },
  'payroll.lastMonth': { ru: 'Прошлый месяц', en: 'Last month' },
  'payroll.custom': { ru: 'Период', en: 'Period' },
  'payroll.total': { ru: 'К выплате', en: 'To pay' },
  'payroll.jobs': { ru: '{count|работа|работы|работ}', en: '{count|job|jobs}' },
  'payroll.unpriced': { ru: '{count} без ставки', en: '{count} without pay set' },
  'payroll.noRate': { ru: 'нет ставки', en: 'no pay set' },
  'nav.staff': { ru: 'Сотрудники', en: 'Staff' },
  'nav.audit': { ru: 'Журнал', en: 'Audit' },
  'nav.settings': { ru: 'Настройки', en: 'Settings' },
  'nav.security': { ru: 'Пароль и вход', en: 'Password and sign-in' },
  'nav.signOut': { ru: 'Выйти', en: 'Sign out' },
  'nav.menu': { ru: 'Меню', en: 'Menu' },
  'nav.soon': { ru: 'СКОРО', en: 'SOON' },

  // ---- common ---------------------------------------------------------------
  'common.save': { ru: 'Сохранить', en: 'Save' },
  'common.cancel': { ru: 'Отмена', en: 'Cancel' },
  'common.add': { ru: 'Добавить', en: 'Add' },
  'common.edit': { ru: 'Изменить', en: 'Edit' },
  'common.remove': { ru: 'Удалить', en: 'Remove' },
  'common.archive': { ru: 'В архив', en: 'Archive' },
  'common.search': { ru: 'Поиск', en: 'Search' },
  'common.reason': { ru: 'Причина', en: 'Reason' },
  'common.note': { ru: 'Заметка', en: 'Note' },
  'common.all': { ru: 'Все', en: 'All' },
  'common.back': { ru: 'Назад', en: 'Back' },
  'common.loading': { ru: 'Загрузка…', en: 'Loading…' },
  'common.nothingYet': { ru: 'Пока ничего нет', en: 'Nothing yet' },
  'common.show': { ru: 'Показывать', en: 'Show' },
  'common.whatIsThis': { ru: 'Что это', en: 'What is this' },
  'common.active': { ru: 'Активен', en: 'Active' },
  'common.blocked': { ru: 'Заблокирован', en: 'Blocked' },
  'common.points': { ru: 'баллы', en: 'points' },
  'common.cashback': { ru: 'кэшбэк', en: 'cashback' },
  'common.phone': { ru: 'Телефон', en: 'Phone' },
  'common.email': { ru: 'Почта', en: 'Email' },
  'common.firstName': { ru: 'Имя', en: 'First name' },
  'common.lastName': { ru: 'Фамилия', en: 'Last name' },
  'common.couldNotLoad': { ru: 'Не удалось загрузить', en: 'Could not load' },

  // ---- sign in --------------------------------------------------------------
  'login.title': { ru: 'Портал сотрудников', en: 'Staff portal' },
  'login.email': { ru: 'Почта', en: 'Email' },
  'login.password': { ru: 'Пароль', en: 'Password' },
  'login.submit': { ru: 'Войти', en: 'Sign in' },
  'login.footer': { ru: 'Auditoria Detailing · Тбилиси', en: 'Auditoria Detailing · Tbilisi' },
  'login.emailRequired': { ru: 'Введите почту', en: 'Enter your email' },
  'login.emailInvalid': { ru: 'Это не похоже на адрес почты', en: 'That does not look like an email' },
  'login.passwordRequired': { ru: 'Введите пароль', en: 'Enter your password' },
  'login.waitBeforeRetry': {
    ru: 'Подождите несколько минут и попробуйте снова.',
    en: 'Wait a few minutes before trying again.',
  },
  'login.code': { ru: 'Код из приложения', en: 'Code from your app' },
  'login.codeAsked': {
    ru: 'Пароль принят. Введите шесть цифр из приложения-аутентификатора.',
    en: 'Password accepted. Enter the six digits from your authenticator app.',
  },
  'login.codeRequired': { ru: 'Введите код', en: 'Enter the code' },

  // ---- second factor --------------------------------------------------------
  'twofa.title': { ru: 'Вход по коду', en: 'Code at sign-in' },
  'twofa.help': {
    ru: 'При входе спрашивается код из приложения на телефоне: Google Authenticator, 1Password или любого другого.',
    en: 'Signing in asks for a code from an app on your phone: Google Authenticator, 1Password or any other.',
  },
  'twofa.begin': { ru: 'Настроить', en: 'Set up' },
  'twofa.scan': {
    ru: 'Наведите камеру приложения на код.',
    en: 'Point your app\u2019s camera at the code.',
  },
  'twofa.qrAlt': { ru: 'Код для аутентификатора', en: 'Code for the authenticator app' },
  'twofa.orType': { ru: 'Или введите ключ:', en: 'Or type the key:' },
  'twofa.codeFromApp': { ru: 'Код из приложения', en: 'Code from the app' },
  'twofa.confirm': { ru: 'Подтвердить', en: 'Confirm' },
  'twofa.on': { ru: 'Включён.', en: 'On.' },
  'twofa.turnOff': { ru: 'Отключить', en: 'Turn off' },
  'twofa.yourPassword': { ru: 'Ваш пароль', en: 'Your password' },
  'twofa.required': {
    ru: 'Для вашей должности это обязательно. Остальные разделы закрыты.',
    en: 'Your role requires this. The rest of the portal is closed.',
  },
  'twofa.setupTitle': { ru: 'Остался один шаг', en: 'One step left' },
  'security.title': { ru: 'Пароль и вход', en: 'Password and sign-in' },

  // ---- the floor: cars here but not yet paid for ---------------------------
  'floor.title': { ru: 'На площадке', en: 'On the floor' },
  'floor.subtitle': {
    ru: 'Сейчас у нас {count|машина|машины|машин}',
    en: '{count|car|cars} here right now',
  },
  'floor.accept': { ru: 'Принять машину', en: 'Take a car in' },
  'floor.empty': { ru: 'Площадка пуста', en: 'Nothing on the floor' },
  'floor.readyGroup': { ru: 'Готовы к выдаче', en: 'Ready to hand back' },
  'floor.workingGroup': { ru: 'В работе', en: 'Being worked on' },
  'floor.ready': { ru: 'Готова', en: 'Ready' },
  'floor.noVehicle': { ru: 'Машина не указана', en: 'No vehicle' },
  'floor.promisedAt': { ru: 'обещали {time}', en: 'promised {time}' },
  'floor.overdueBy': { ru: 'ждут с {time}', en: 'waiting since {time}' },
  'floor.doneOf': { ru: 'сделано {done} из {total}', en: '{done} of {total} done' },
  'floor.unassigned': { ru: 'Никому', en: 'Nobody' },
  'floor.reopen': { ru: 'Вернуть в работу', en: 'Reopen' },
  'floor.handOver': { ru: 'Выдать и рассчитать', en: 'Hand over and charge' },
  'floor.cancel': { ru: 'Отменить', en: 'Call off' },
  'floor.cancelReason': { ru: 'Почему отменяем', en: 'Why it is being called off' },
  'floor.cancelConfirm': { ru: 'Отменить машину', en: 'Call the car off' },

  'floor.tabHere': { ru: 'На площадке', en: 'Here now' },
  'floor.tabExpected': { ru: 'Ждём', en: 'Expected' },

  // ---- кого ждём ------------------------------------------------------------
  'booking.new': { ru: 'Записать клиента', en: 'Book somebody in' },
  'booking.edit': { ru: 'Поправить запись', en: 'Edit the booking' },
  'booking.add': { ru: 'Записать', en: 'Book' },
  'booking.name': { ru: 'Кого ждём', en: 'Who is expected' },
  'booking.when': { ru: 'Когда ждём', en: 'When' },
  'booking.phone': { ru: 'Телефон', en: 'Phone' },
  'booking.vehicle': { ru: 'Машина', en: 'Vehicle' },
  'booking.request': { ru: 'Что хотят', en: 'What they want' },
  'booking.arrived': { ru: 'Приехал', en: 'Arrived' },
  'booking.noShow': { ru: 'Не пришёл', en: 'No-show' },
  'booking.cancel': { ru: 'Отменить', en: 'Call off' },
  'booking.late': { ru: 'Опаздывает', en: 'Late' },
  'booking.fromBooking': { ru: 'По записи', en: 'From the booking' },
  'booking.findOrCreate': {
    ru: 'Карточка не привязана. Найдите по имени, телефону или госномеру — или заведите нового клиента.',
    en: 'No card linked yet. Search by name, phone or plate — or create the customer.',
  },
  'booking.empty': { ru: 'Никого не ждём', en: 'Nobody expected' },

  'floor.acceptTitle': { ru: 'Приёмка машины', en: 'Taking a car in' },
  'floor.whoseCar': { ru: 'Чья машина', en: 'Whose car' },
  'floor.whichCar': { ru: 'Какая машина', en: 'Which car' },
  'floor.whatToDo': { ru: 'Что делаем', en: 'What is being done' },
  'floor.oneOffName': { ru: 'Какая работа', en: 'Which job' },
  'floor.addWork': { ru: 'Добавить работу', en: 'Add a job' },
  'floor.addWorkConfirm': { ru: 'Добавить', en: 'Add' },
  'floor.whichCarQuestion': { ru: 'Какая машина приехала?', en: 'Which car is it?' },
  'floor.noWork': { ru: 'Работы не добавлены', en: 'No jobs yet' },
  'floor.who': { ru: 'Кто делает', en: 'Who does it' },
  'floor.promise': { ru: 'Обещали к', en: 'Promised by' },
  'floor.setPromise': { ru: 'Указать срок', en: 'Set a time' },
  'floor.clearPromise': { ru: 'Убрать срок', en: 'Clear the time' },
  'due.day': { ru: 'День', en: 'Day' },
  'due.time': { ru: 'Время', en: 'Time' },
  'due.today': { ru: 'Сегодня', en: 'Today' },
  'due.tomorrow': { ru: 'Завтра', en: 'Tomorrow' },
  'due.afterTomorrow': { ru: 'Послезавтра', en: 'Day after' },
  'due.otherDay': { ru: 'Другой день', en: 'Other day' },
  'due.otherTime': { ru: 'Другое время', en: 'Other time' },
  'due.pickTime': { ru: 'Выберите время', en: 'Pick a time' },
  'floor.intakeNotes': { ru: 'Что заметили при приёмке', en: 'Noted at intake' },
  'floor.acceptSubmit': { ru: 'Принять машину', en: 'Take the car in' },
  'floor.accepted': { ru: 'Машина принята, номер {reference}', en: 'Car taken in as {reference}' },
  'floor.fromIntake': {
    ru: 'Машина с площадки: номер и время приёмки сохранятся.',
    en: 'A car from the floor: its number and intake time carry over.',
  },

  'work.title': { ru: 'Моя работа', en: 'My work' },
  'work.empty': { ru: 'Пока ничего', en: 'Nothing yet' },
  'work.start': { ru: 'Взял в работу', en: 'Start' },
  'work.finish': { ru: 'Готово', en: 'Finished' },
  'work.stop': { ru: 'Отложить', en: 'Put down' },
  'work.running': { ru: 'В работе', en: 'Running' },
  'install.title': { ru: 'Портал на телефоне', en: 'The portal on your phone' },
  'install.button': { ru: 'Установить', en: 'Install' },
  'install.ios1': { ru: 'Нажмите «Поделиться» внизу экрана', en: 'Tap Share at the bottom of the screen' },
  'install.ios2': { ru: 'Выберите «На экран „Домой“»', en: 'Choose "Add to Home Screen"' },
  'install.ios3': { ru: 'Включите «Открывать как веб-приложение» и нажмите «Добавить»', en: 'Turn on "Open as Web App" and tap Add' },
  'install.ios4': { ru: 'Откройте Auditoria с иконки и нажмите «Включить»', en: 'Open Auditoria from the icon and tap Turn on' },

  'push.on': { ru: 'Уведомления на этом телефоне включены', en: 'Notifications on for this phone' },
  'push.off': { ru: 'Уведомления', en: 'Notifications' },
  'push.how': { ru: 'Как включить', en: 'How' },
  'push.howTitle': { ru: 'Уведомления на iPhone', en: 'Notifications on iPhone' },
  'push.turnOn': { ru: 'Включить', en: 'Turn on' },
  'push.turnOff': { ru: 'Выключить', en: 'Turn off' },
  'push.blocked': {
    ru: 'Уведомления запрещены в настройках телефона',
    en: 'Notifications are blocked in the phone settings',
  },
  'work.status.PENDING': { ru: 'Ждёт', en: 'Waiting' },
  'work.status.IN_PROGRESS': { ru: 'В работе', en: 'Running' },
  'work.status.DONE': { ru: 'Готово', en: 'Done' },

  'password.title': { ru: 'Смена пароля', en: 'Change password' },
  'password.forced': { ru: 'Пароль временный. Задайте свой.', en: 'This password is temporary. Set your own.' },
  'password.current': { ru: 'Текущий пароль', en: 'Current password' },
  'password.new': { ru: 'Новый пароль', en: 'New password' },
  'password.repeat': { ru: 'Ещё раз', en: 'Repeat it' },
  'password.rule': { ru: 'Не короче 12 символов', en: 'At least 12 characters' },
  'password.mismatch': { ru: 'Пароли не совпадают', en: 'The passwords do not match' },
  'password.submit': { ru: 'Сменить пароль', en: 'Change the password' },
  'password.signOutInstead': { ru: 'Выйти вместо этого', en: 'Sign out instead' },

  // ---- dashboard ------------------------------------------------------------
  'dash.today': { ru: 'Сегодня', en: 'Today' },
  'dash.greeting': { ru: 'Здравствуйте, {name}', en: 'Hello, {name}' },
  'dash.revenueToday': { ru: 'Выручка за сегодня', en: 'Revenue today' },
  'dash.cashbackToday': { ru: 'Кэшбэк за сегодня', en: 'Cashback today' },
  'dash.customers': { ru: 'Клиенты', en: 'Customers' },
  'dash.loyaltyOwed': { ru: 'Обязательства', en: 'Loyalty owed' },
  'dash.revenueByDay': { ru: 'Выручка по дням', en: 'Revenue by day' },
  'dash.last14': { ru: 'Последние 14 дней', en: 'Last 14 days' },
  'dash.tierDistribution': { ru: 'Распределение по уровням', en: 'Tier distribution' },
  'dash.recentOrders': { ru: 'Последние заказы', en: 'Recent orders' },
  'dash.goneQuiet': { ru: 'Давно не приходили', en: 'Gone quiet' },
  'dash.quiet.WARNING': { ru: 'ДАВНО НЕ БЫЛ', en: 'AWAY' },
  'dash.quiet.LAPSED': { ru: 'УШЁЛ', en: 'LAPSED' },
  'dash.quiet.LOST': { ru: 'ПОТЕРЯН', en: 'LOST' },
  'dash.noOrdersYet': { ru: 'Завершённых заказов пока нет', en: 'No completed orders yet' },
  'dash.nobodyQuiet': {
    ru: 'Никто с тратами выше {amount} не пропадал дольше {days|дня|дней|дней}.',
    en: 'No customer above {amount} has been away for {days} days.',
  },
  'customers.noMatch': { ru: 'Клиентов по запросу «{query}» нет', en: 'No customers match “{query}”' },
  'orders.noMatch': { ru: 'Заказов по запросу «{query}» нет', en: 'No orders match “{query}”' },
  'veh.archiveAria': { ru: 'Отправить в архив {make} {model}', en: 'Archive {make} {model}' },
  'adjust.recorded': {
    ru: 'Записано. Уровень теперь {tier}{changed}.',
    en: 'Recorded. Tier is now {tier}{changed}.',
  },
  'adjust.tierChanged': { ru: ' — он изменился', en: ' — it changed' },
  'dash.ordersCount': { ru: '{count|заказ|заказа|заказов}', en: '{count|order|orders}' },
  'dash.newToday': { ru: '{count|новый|новых|новых} сегодня', en: '{count} new today' },
  'dash.noneRedeemed': { ru: 'ничего не списано', en: 'none redeemed' },
  'dash.redeemed': { ru: '{amount} списано', en: '{amount} redeemed' },
  'dash.noneNewToday': { ru: 'новых сегодня нет', en: 'none new today' },
  'dash.plusMaturing': { ru: 'и ещё {amount} созревает', en: 'plus {amount} maturing' },
  'dash.pointsOutstanding': { ru: '{points} баллов на руках', en: '{points} points outstanding' },

  // ---- customers ------------------------------------------------------------
  'customers.title': { ru: 'Клиенты', en: 'Customers' },
  'customers.search': { ru: 'Найти клиента', en: 'Search customers' },
  'customers.searchPlaceholder': {
    ru: 'Георгий, 555001234, AUD-…, XX123XX',
    en: 'Giorgi, 555001234, AUD-…, XX123XX',
  },
  'customers.new': { ru: 'Новый клиент', en: 'New customer' },
  'customers.create': { ru: 'Завести клиента', en: 'Create customer' },
  'customers.referredBy': { ru: 'Кто пригласил', en: 'Referred by' },
  'customers.referralCodePlaceholder': { ru: 'код друга', en: 'friend’s code' },
  'customers.firstVehicle': { ru: 'Первая машина (необязательно)', en: 'First vehicle (optional)' },
  'customers.make': { ru: 'Марка', en: 'Make' },
  'customers.model': { ru: 'Модель', en: 'Model' },
  'customers.plate': { ru: 'Госномер', en: 'Plate' },
  'customers.lifetime': { ru: 'за всё время', en: 'lifetime' },
  'customers.openThem': { ru: 'Открыть {name}', en: 'Open {name}' },

  // ---- orders ---------------------------------------------------------------
  'orders.title': { ru: 'Заказы', en: 'Orders' },
  'orders.search': { ru: 'Найти заказ', en: 'Search orders' },
  'orders.searchPlaceholder': { ru: 'ORD-2026-…, Георгий, AUD-…', en: 'ORD-2026-…, Giorgi, AUD-…' },
  'orders.value': { ru: 'сумма', en: 'value' },
  'orders.large': { ru: 'КРУПНЫЙ', en: 'LARGE' },
  'orders.earned': { ru: 'начислено', en: 'earned' },

  // ---- follow-ups -----------------------------------------------------------
  'followups.title': { ru: 'Напоминания', en: 'Follow-ups' },
  'followups.dueNow': { ru: 'Пора сейчас', en: 'Due now' },
  'followups.dueNowSub': { ru: 'стоит позвонить', en: 'worth a call today' },
  'followups.upcoming': { ru: 'Скоро', en: 'Coming up' },
  'followups.upcomingSub': { ru: 'срок ещё не наступил', en: 'not yet due' },
  'followups.horizonNow': { ru: 'Уже пора', en: 'Due now' },
  'followups.horizon7': { ru: 'Ближайшие 7 дней', en: 'Next 7 days' },
  'followups.horizon14': { ru: 'Ближайшие 14 дней', en: 'Next 14 days' },
  'followups.horizon30': { ru: 'Ближайшие 30 дней', en: 'Next 30 days' },
  'followups.empty': { ru: 'В этом окне никого нет', en: 'Nobody is due in that window' },
  'followups.count': { ru: '{count|напоминание|напоминания|напоминаний}', en: '{count|reminder|reminders}' },
  'followups.call': { ru: 'Позвонить', en: 'Call' },
  'followups.dismiss': { ru: 'Снять', en: 'Dismiss' },
  'followups.due': { ru: 'пора', en: 'due' },
  'followups.speaks': { ru: 'язык: {lang}', en: 'speaks {lang}' },

  // ---- vehicles -------------------------------------------------------------
  'vehicles.title': { ru: 'Автопарк', en: 'Vehicles' },
  'vehicles.searchPlaceholder': { ru: 'AA111AA, BMW или имя владельца', en: 'AA111AA, BMW, or the owner’s name' },
  'vehicles.search': { ru: 'Найти машину', en: 'Search vehicles' },
  'vehicles.includeArchived': { ru: 'И архивные', en: 'Archived too' },
  'vehicles.archived': { ru: 'в архиве', en: 'archived' },
  'vehicles.emptySearch': { ru: 'Таких машин нет', en: 'No car matches that' },
  'vehicles.empty': { ru: 'Машин пока нет', en: 'No vehicles recorded yet' },
  'vehicles.count': { ru: '{count|машина|машины|машин}', en: '{count|vehicle|vehicles}' },
  'vehicles.noPlate': { ru: 'без госномера', en: 'no plate' },
  'vehicles.jobs': { ru: '{count|работа|работы|работ}', en: '{count|job|jobs}' },
  'vehicles.neverIn': { ru: 'не был', en: 'never in' },

  // ---- wallets --------------------------------------------------------------
  'wallets.title': { ru: 'Карты в телефоне', en: 'Wallets' },
  'wallets.mockWarning': { ru: '{providers}: заглушка, карты не установятся на телефон.', en: '{providers}: stand-in provider, passes will not install on a phone.' },
  'wallets.live': { ru: 'В телефоне', en: 'Live' },
  'wallets.liveSub': { ru: 'установлены', en: 'on a phone' },
  'wallets.queued': { ru: 'В очереди', en: 'Queued' },
  'wallets.queuedSub': { ru: 'ждут выпуска', en: 'waiting to issue' },
  'wallets.failing': { ru: 'С ошибкой', en: 'Failing' },
  'wallets.failingSub': { ru: 'нужна повторная синхронизация', en: 'need a re-sync' },
  'wallets.revoked': { ru: 'Отозваны', en: 'Revoked' },
  'wallets.revokedSub': { ru: 'сняты', en: 'pulled' },
  'wallets.filterAll': { ru: 'Все', en: 'Everything' },
  'wallets.filterNotIssued': { ru: 'Ждут выпуска', en: 'Waiting to issue' },
  'wallets.filterIssued': { ru: 'Выпущены', en: 'Issued' },
  'wallets.filterActive': { ru: 'Активны', en: 'Active' },
  'wallets.filterError': { ru: 'С ошибкой', en: 'Failing' },
  'wallets.filterRevoked': { ru: 'Отозваны', en: 'Revoked' },
  'wallets.emptyFiltered': { ru: 'В этом состоянии ничего нет', en: 'Nothing in that state' },
  'wallets.empty': { ru: 'Карты ещё не выпускались', en: 'No passes issued yet' },
  'wallets.count': { ru: '{count|карта|карты|карт}', en: '{count|pass|passes}' },
  'wallets.resync': { ru: 'Синхронизировать', en: 'Re-sync' },
  'wallets.revoke': { ru: 'Отозвать', en: 'Revoke' },
  'wallets.revokeReason': { ru: 'почему карта отзывается', en: 'why this pass is being pulled' },
  'wallets.revokeWarning': { ru: 'Карта перестанет работать у клиента.', en: 'The card stops working on their phone.' },
  'wallets.synced': { ru: 'обновлена {when}', en: 'synced {when}' },
  'wallets.waitingToIssue': { ru: 'ждёт выпуска', en: 'waiting to be issued' },

  // ---- scan -----------------------------------------------------------------
  'scan.title': { ru: 'Сканировать клиента', en: 'Scan customer' },
  'scan.cameraPreview': { ru: 'Изображение с камеры', en: 'Camera preview' },
  'scan.denied': { ru: 'Доступ к камере запрещён.', en: 'Camera access is blocked.' },
  'scan.unavailable': { ru: 'На этом устройстве нет камеры.', en: 'No camera on this device.' },
  'scan.timeout': { ru: 'Камера не отвечает.', en: 'The camera is not responding.' },
  'scan.insecure': { ru: 'Камере нужно соединение по HTTPS.', en: 'The camera needs an HTTPS connection.' },
  'scan.switchCamera': { ru: 'Другая камера ({current}/{total})', en: 'Switch camera ({current}/{total})' },
  'scan.orEnterCode': { ru: 'Или введите код', en: 'Or enter the code' },
  'scan.code': { ru: 'Код', en: 'Code' },
  'scan.codePlaceholder': { ru: 'вставьте или наберите код', en: 'paste or type the token' },
  'scan.find': { ru: 'Найти', en: 'Find' },
  'scan.newOrder': { ru: 'Новый заказ', en: 'New order' },
  'scan.again': { ru: 'Сканировать ещё', en: 'Scan again' },
  'scan.rate': { ru: 'ставка', en: 'rate' },
  'scan.vehicles': { ru: 'машины', en: 'vehicles' },

  // ---- order ----------------------------------------------------------------
  'order.couldNotLoad': { ru: 'Не удалось загрузить заказ.', en: 'That order could not be loaded.' },
  'order.printReceipt': { ru: 'Печать чека', en: 'Print receipt' },
  'order.hideReceipt': { ru: 'Скрыть чек', en: 'Hide receipt' },
  'order.services': { ru: 'Услуги', en: 'Services' },
  'order.note': { ru: 'Заметка', en: 'Note' },
  'order.payments': { ru: 'Оплата', en: 'Payments' },
  'order.refund': { ru: 'Возврат', en: 'Refund' },
  'order.refundHistory': { ru: 'История возвратов', en: 'Refund history' },
  'order.fullyRefunded': { ru: 'По этому заказу возвращено всё.', en: 'Everything on this order has been refunded.' },
  'order.totals': { ru: 'Итого', en: 'Totals' },
  'order.subtotal': { ru: 'Сумма услуг', en: 'Subtotal' },
  'order.discount': { ru: 'Скидка {percent}%', en: 'Discount {percent}%' },
  'order.cashbackApplied': { ru: 'Списано кэшбэка', en: 'Cashback applied' },
  'order.value': { ru: 'Сумма заказа', en: 'Order value' },
  'order.cashbackEarned': { ru: 'Начислено кэшбэка', en: 'Cashback earned' },
  'order.pointsEarned': { ru: 'Начислено баллов', en: 'Points earned' },
  'order.rulesVersion': { ru: 'Рассчитан по правилам', en: 'Priced under rules' },
  'order.refunded': { ru: 'Возвращено', en: 'Refunded' },
  'order.selectAllRefundable': { ru: 'Выбрать всё, что можно вернуть', en: 'Select everything refundable' },
  'order.refundReason': { ru: 'почему делается возврат', en: 'why this is being refunded' },
  'order.reasonAudited': {
    ru: 'Записывается в журнал с вашим именем',
    en: 'Recorded in the audit log against your account',
  },
  'order.confirmFull': { ru: 'Вернуть заказ целиком?', en: 'Refund the whole order?' },
  'order.confirmPartial': { ru: 'Вернуть выбранные позиции?', en: 'Refund these lines?' },
  'order.confirmRefund': { ru: 'Подтвердить возврат', en: 'Confirm refund' },
  'order.refundAmount': { ru: 'Вернуть {amount}', en: 'Refund {amount}' },
  'order.notCompleted': { ru: 'не проведён', en: 'not completed' },
  'order.acceptedAt': { ru: 'принята {time}', en: 'taken in {time}' },
  'order.on': { ru: 'мастер: {name}', en: 'with {name}' },
  'order.by': { ru: 'выполнил {name}', en: 'by {name}' },
  'order.refundable': { ru: 'можно вернуть: {count}', en: '{count} refundable' },

  // ---- new order ------------------------------------------------------------
  'neworder.title': { ru: 'Новый заказ', en: 'New order' },
  'neworder.whoFor': { ru: 'Для кого заказ', en: 'Who is this for' },
  'neworder.findCustomer': { ru: 'Найти клиента', en: 'Find customer' },
  'neworder.findPlaceholder': {
    ru: 'Имя, телефон, номер карты или госномер',
    en: 'Name, phone, loyalty ID or plate',
  },
  'neworder.startTyping': {
    ru: 'Начните вводить или отсканируйте карту клиента.',
    en: 'Start typing, or scan the customer’s card.',
  },
  'neworder.createCustomer': { ru: 'Завести клиента', en: 'Create a customer' },
  'neworder.savedBaskets': { ru: 'Отложенные корзины', en: 'Saved baskets' },
  'neworder.lines': { ru: '{count|позиция|позиции|позиций}', en: '{count|line|lines}' },
  'neworder.saveForLater': { ru: 'Отложить', en: 'Save for later' },
  'neworder.updateBasket': { ru: 'Обновить отложенное', en: 'Update saved basket' },
  'neworder.discard': { ru: 'Удалить', en: 'Discard' },
  'neworder.changeCustomer': { ru: 'Другой клиент', en: 'Change customer' },
  'neworder.fromBasket': { ru: 'Из отложенной корзины.', en: 'From a saved basket.' },
  'neworder.blockedWarning': { ru: 'Клиент заблокирован: начислений не будет.', en: 'Customer is blocked: nothing will be earned.' },
  'neworder.vehicle': { ru: 'Машина', en: 'Vehicle' },
  'neworder.services': { ru: 'Услуги', en: 'Services' },
  'neworder.addService': { ru: '+ Услуга', en: '+ Service' },
  'neworder.noServices': { ru: 'Пока ничего не добавлено', en: 'No services yet' },
  'neworder.addFirstService': { ru: 'Добавить первую услугу', en: 'Add the first service' },
  'neworder.service': { ru: 'Услуга', en: 'Service' },
  'neworder.oneOff': { ru: 'Другое — впишу сам', en: 'Something else — I will type it' },
  'neworder.oneOffName': { ru: 'Что сделали', en: 'What was done' },
  'picker.choose': { ru: 'Выбрать услугу', en: 'Choose a service' },
  'picker.search': { ru: 'Начните печатать название', en: 'Start typing a name' },
  'picker.oneOffNamed': { ru: 'Другое: «{name}»', en: 'Something else: “{name}”' },
  'neworder.staffPay': { ru: 'Мастеру, ₾', en: 'Technician pay, ₾' },
  'pay.label': { ru: 'Зарплата', en: 'Pay' },
  'pay.notSet': { ru: 'не указана', en: 'not set' },
  'pay.set': { ru: 'Указать', en: 'Set' },
  'pay.afterRefund': { ru: 'после возврата {amount}', en: '{amount} after refund' },
  'pay.orderTotal': { ru: 'Зарплаты мастерам', en: 'Staff pay' },
  'pay.column': { ru: 'Мастерам', en: 'Staff pay' },
  'pay.today': { ru: 'Зарплаты сегодня', en: 'Staff pay today' },
  'pay.month': { ru: 'С начала месяца', en: 'Month to date' },
  'pay.dashboardTitle': { ru: 'Зарплаты мастерам', en: 'Staff pay' },
  'pay.nobodyYet': { ru: 'В этом месяце зарплат ещё нет.', en: 'No staff pay this month yet.' },
  'neworder.staffPayFixed': { ru: 'по фиксе из каталога', en: 'catalogue fixed pay' },
  'neworder.repeatLast': { ru: 'Как {date}', en: 'Same as {date}' },
  'neworder.qty': { ru: 'Кол-во', en: 'Qty' },
  'neworder.priceCharged': { ru: 'Цена к оплате', en: 'Price charged' },
  'neworder.removeService': { ru: 'Убрать услугу', en: 'Remove service' },
  'neworder.performedBy': { ru: 'Кто выполнял', en: 'Performed by' },
  'neworder.notRecorded': { ru: '— не указано —', en: '— not recorded —' },
  'neworder.catalogFrom': { ru: 'в каталоге от {price}', en: 'catalog starts at {price}' },
  'neworder.noCatalogPrice': { ru: 'цена в каталоге не указана', en: 'no published starting price' },
  'neworder.note': { ru: 'Заметка', en: 'Note' },
  'neworder.notePlaceholder': {
    ru: 'Что стоит записать по этой работе',
    en: 'Anything worth recording against this job',
  },
  'neworder.cashbackAndDiscount': { ru: 'Кэшбэк и скидка', en: 'Cashback and discount' },
  'neworder.applyCashback': { ru: 'Списать кэшбэк', en: 'Apply cashback' },
  'neworder.upTo': { ru: 'до {amount}', en: 'up to {amount}' },
  'neworder.max': { ru: 'Максимум {amount}', en: 'Max {amount}' },
  'neworder.clear': { ru: 'Сбросить', en: 'Clear' },
  'neworder.discount': { ru: 'Скидка', en: 'Discount' },
  'neworder.summary': { ru: 'Итого', en: 'Summary' },
  'neworder.subtotal': { ru: 'Сумма услуг', en: 'Subtotal' },
  'neworder.discountLine': { ru: 'Скидка {percent}%', en: 'Discount {percent}%' },
  'neworder.cashbackApplied': { ru: 'Списано кэшбэка', en: 'Cashback applied' },
  'neworder.toPay': { ru: 'К оплате', en: 'To pay' },
  'neworder.willOwe': { ru: 'Долг', en: 'Owed' },
  'neworder.earnsAt': { ru: 'Начислится по ставке {rate}', en: 'Earns at {rate}' },
  'neworder.points': { ru: 'Баллы', en: 'Points' },
  'neworder.tierAfter': { ru: 'Уровень после', en: 'Tier after' },
  'neworder.addServiceToSeeTotal': { ru: 'Добавьте услугу, чтобы увидеть итог.', en: 'Add a service to see the total.' },
  'neworder.enterPrice': { ru: 'Впишите цену, чтобы увидеть итог.', en: 'Enter a price to see the total.' },
  'neworder.payment': { ru: 'Оплата', en: 'Payment' },
  'neworder.card': { ru: 'Карта', en: 'Card' },
  'neworder.cash': { ru: 'Наличные', en: 'Cash' },
  'neworder.transfer': { ru: 'Перевод', en: 'Transfer' },
  'neworder.other': { ru: 'Другое', en: 'Other' },
  'neworder.complete': { ru: 'Провести', en: 'Complete' },
  'neworder.completeAmount': { ru: 'Провести · {amount}', en: 'Complete · {amount}' },
  'neworder.blocked': { ru: 'заблокирован', en: 'blocked' },
  'neworder.noCashbackOrPoints': { ru: 'ничего не начислится', en: 'no cashback or points' },
  'neworder.completed': { ru: 'Заказ проведён', en: 'Order completed' },
  'neworder.print': { ru: 'Печать', en: 'Print' },
  'neworder.tierIsNow': { ru: 'Уровень теперь {tier}.', en: 'Tier is now {tier}.' },

  // ---- staff ----------------------------------------------------------------
  'staff.title': { ru: 'Сотрудники', en: 'Staff' },
  'staff.addPerson': { ru: 'Добавить человека', en: 'Add person' },
  'staff.newPerson': { ru: 'Новый сотрудник', en: 'New person' },
  'staff.empty': { ru: 'Пока никого нет', en: 'Nobody added yet' },
  'staff.showInactive': { ru: 'Показывать отключённых', en: 'Show inactive' },
  'staff.jobTitle': { ru: 'Должность', en: 'Job title' },
  'staff.jobTitlePlaceholder': { ru: 'Старший мастер', en: 'Senior detailer' },
  'staff.positionNamePlaceholder': { ru: 'Мастер детейлинга', en: 'Detailer' },
  'staff.canSignIn': { ru: 'Этот человек входит в портал', en: 'This person signs in to the staff portal' },
  'staff.password': { ru: 'Пароль', en: 'Password' },
  'staff.passwordHint': {
    ru: 'Не короче 12 символов; меняется при первом входе',
    en: 'At least 12 characters; changed at first sign-in',
  },
  'staff.passwordKeep': { ru: 'оставьте пустым, чтобы не менять', en: 'leave blank to keep the current one' },
  'staff.resetPassword': { ru: 'Сбросить пароль', en: 'Reset password' },
  'staff.resetHint': { ru: 'Завершит сессии и потребует сменить пароль.', en: 'Ends their sessions and forces a new password.' },
  'staff.positions': { ru: 'Должности', en: 'Positions' },
  'staff.code': { ru: 'Код', en: 'Code' },
  'staff.name': { ru: 'Название', en: 'Name' },
  'staff.description': { ru: 'Описание', en: 'Description' },
  'staff.descriptionPlaceholder': { ru: 'для чего эта должность', en: 'what this position is for' },
  'staff.ownerLocked': {
    ru: 'У владельца всегда все права, их нельзя урезать.',
    en: 'The owner position always holds every permission, so it cannot be trimmed.',
  },

  // ---- customer page --------------------------------------------------------
  'customer.couldNotLoad': { ru: 'Не удалось загрузить клиента.', en: 'That customer could not be loaded.' },
  'customer.newOrder': { ru: 'Новый заказ', en: 'New order' },
  'customer.lifetimeSpend': { ru: 'Потрачено за всё время', en: 'Lifetime spend' },
  'customer.cashback': { ru: 'Кэшбэк', en: 'Cashback' },
  'customer.points': { ru: 'Баллы', en: 'Points' },
  'customer.orders': { ru: 'Заказы', en: 'Orders' },
  'customer.visits': { ru: 'Визиты', en: 'Visits' },
  'customer.noVisits': { ru: 'Визитов пока нет', en: 'No visits yet' },
  'customer.visitsEmpty': { ru: 'Завершённые заказы появятся здесь.', en: 'Completed orders will appear here.' },
  'customer.avg': { ru: 'в среднем {amount}', en: 'avg {amount}' },
  'customer.earnsAt': { ru: 'ставка {rate}', en: 'earns {rate}' },
  'customer.perLari': { ru: '{multiplier} за лари', en: '{multiplier} per lari' },
  'customer.topTier': { ru: 'высший уровень', en: 'top tier' },
  'customer.permanent': { ru: 'закреплён навсегда', en: 'permanent' },
  'customer.notes': { ru: 'Внутренние заметки', en: 'Internal notes' },
  'customer.referrals': { ru: 'Приглашения', en: 'Referrals' },
  'customer.theirCode': { ru: 'Его код', en: 'Their code' },
  'customer.broughtPeople': { ru: 'Привёл людей', en: 'People they brought' },
  'customer.invitedBy': { ru: 'Пригласил', en: 'Invited by' },
  'customer.bonusNotPaid': { ru: 'бонус ещё не начислен', en: 'bonus not paid yet' },

  // ---- merge ----------------------------------------------------------------
  'merge.duplicates': { ru: 'Дубликаты', en: 'Duplicates' },
  'merge.intro': { ru: 'Присоедините дубликат к этой записи.', en: 'Attach a duplicate to this record.' },
  'merge.start': { ru: 'Присоединить дубликат', en: 'Merge a duplicate in' },
  'merge.into': { ru: 'Присоединить к {name}', en: 'Merge into {name}' },
  'merge.findPlaceholder': { ru: 'Найти вторую запись', en: 'Find the duplicate record' },
  'merge.find': { ru: 'Найти дубликат', en: 'Find duplicate' },
  'merge.nobodyMatches': { ru: 'Больше никто не подходит.', en: 'Nobody else matches.' },
  'merge.working': { ru: 'Считаем результат…', en: 'Working out the result…' },
  'merge.foldedIn': { ru: 'присоединяется', en: 'folded in' },
  'merge.survives': { ru: 'остаётся', en: 'survives' },
  'merge.lifetime': { ru: 'потрачено', en: 'lifetime' },
  'merge.cashback': { ru: 'кэшбэк', en: 'cashback' },
  'merge.points': { ru: 'баллы', en: 'points' },
  'merge.records': { ru: 'записи', en: 'records' },
  'merge.result': { ru: 'Результат', en: 'Result' },
  'merge.resultLifetime': { ru: 'Потрачено за всё время', en: 'Lifetime spend' },
  'merge.resultCashback': { ru: 'Кэшбэк', en: 'Cashback' },
  'merge.resultPoints': { ru: 'Баллы', en: 'Points' },
  'merge.tier': { ru: 'Уровень', en: 'Tier' },
  'merge.reasonPlaceholder': { ru: 'почему это один человек', en: 'why these are the same person' },
  'merge.confirm': { ru: 'Объединить записи', en: 'Merge these records' },
  'merge.confirmQuestion': { ru: 'Присоединить {id}?', en: 'Fold {id} in?' },
  'merge.point1': { ru: '· заказы, машины, коды наград и заметки переедут', en: '· orders, vehicles, reward codes and notes move across' },
  'merge.point2': { ru: '· остатки перенесутся записями, обе истории останутся читаемыми', en: '· balances transfer as ledger entries, so both histories stay readable' },
  'merge.point3': { ru: '· уровень, закреплённый навсегда у любой из записей, сохранится', en: '· a tier held permanently by either record is kept' },
  'merge.point4': { ru: '· старая запись останется помеченной и будет указывать сюда', en: '· the old record stays, marked merged and pointing here' },
  'merge.point5': { ru: '· её QR-коды, сессии и карты в телефоне перестанут работать', en: '· its QR codes, sessions and wallet passes stop working' },
  'merge.do': { ru: 'Объединить', en: 'Merge' },
  'merge.pickAnother': { ru: 'Выбрать другую запись', en: 'Pick a different record' },

  // ---- rewards --------------------------------------------------------------
  'rewards.title': { ru: 'Награды', en: 'Rewards' },
  'rewards.empty': { ru: 'Наград пока нет', en: 'No rewards yet' },
  'rewards.nameKa': { ru: 'Название (ka)', en: 'Name (ka)' },
  'rewards.nameRu': { ru: 'Название (ru)', en: 'Name (ru)' },
  'rewards.nameEn': { ru: 'Название (en)', en: 'Name (en)' },
  'rewards.points': { ru: 'Баллы', en: 'Points' },
  'rewards.limitPerCustomer': { ru: 'Лимит на клиента', en: 'Limit per customer' },
  'rewards.validForDays': { ru: 'Срок действия (дней)', en: 'Valid for (days)' },
  'rewards.noLimit': { ru: 'без лимита', en: 'no limit' },
  'rewards.noExpiry': { ru: 'без срока', en: 'no expiry' },
  'rewards.issuedCodes': { ru: 'Выданные коды', en: 'Issued codes' },
  'rewards.redeemCode': { ru: 'Погасить код', en: 'Redeem a code' },
  'rewards.code': { ru: 'Код', en: 'Code' },
  'rewards.nothingHere': { ru: 'Здесь пусто', en: 'Nothing here' },

  'staff.addPosition': { ru: '+ Должность', en: '+ Position' },
  'staff.builtIn': { ru: 'ВСТРОЕННАЯ', en: 'BUILT-IN' },
  'staff.permissionsCount': { ru: '{count|право|права|прав}', en: '{count|permission|permissions}' },
  'staff.peopleCount': { ru: '{count|человек|человека|человек}', en: '{count|person|people}' },
  'staff.manage': { ru: 'Управление', en: 'Manage' },
  'staff.lastSignedIn': { ru: 'последний вход {when}', en: 'last signed in {when}' },
  'staff.neverSignedIn': { ru: 'ни разу не входил', en: 'never signed in' },
  'staff.jobs': { ru: '{count|работа|работы|работ}', en: '{count|job|jobs}' },
  'staff.inactive': { ru: 'отключён', en: 'inactive' },
  'staff.noLogin': { ru: 'без входа', en: 'no sign-in' },
  'staff.close': { ru: 'Закрыть', en: 'Close' },
  'staff.workRecorded': {
    ru: 'на этого человека записываются работы',
    en: 'work is recorded against this person',
  },
  'roleDesc.OWNER': { ru: 'Полный доступ; импорт и выгрузка данных — только у владельца', en: 'Full access; imports and data exports are the owner’s alone' },
  'roleDesc.ADMIN': { ru: 'Сотрудники, настройки и правила лояльности', en: 'Staff, settings and loyalty configuration' },
  'roleDesc.MANAGER': { ru: 'Возвраты, корректировки и отчёты', en: 'Refunds, adjustments and reports' },
  'roleDesc.CASHIER': { ru: 'Заказы, списание кэшбэка и выдача наград', en: 'Orders, cashback redemption and rewards' },
  'roleDesc.STAFF': { ru: 'Только просмотр клиентов и заказов', en: 'Read-only access to customers and orders' },

  // ---- customer access ------------------------------------------------------
  'access.title': { ru: 'Доступ в кабинет', en: 'Portal access' },
  'access.issue': { ru: 'Выдать ссылку для входа', en: 'Issue a sign-in link' },
  'access.newLink': { ru: 'Новая ссылка', en: 'New link' },
  'access.pointCamera': {
    ru: 'Пусть клиент наведёт камеру телефона на этот код.',
    en: 'Have the customer point their phone camera at this code.',
  },
  'access.singleUse': {
    ru: 'Одноразовая. Новая ссылка отменяет эту.',
    en: 'Single use. Issuing a new one stops this link working.',
  },
  'access.codesOff': { ru: 'Коды отключены — вход по ссылке.', en: 'Codes are off — sign-in is by link.' },

  // ---- printed receipt ------------------------------------------------------
  // ---- оплата после заказа ---------------------------------------------------
  'payment.owedTitle': { ru: 'Оплата', en: 'Payment' },
  'payment.nothingPaid': { ru: 'Заказ не оплачен', en: 'Nothing paid yet' },
  'payment.partlyPaid': { ru: 'Внесено {paid} из {total}', en: '{paid} of {total} paid' },
  'payment.amount': { ru: 'Сколько вносим', en: 'How much' },
  'payment.upTo': { ru: 'Не больше {amount}', en: 'No more than {amount}' },
  'payment.take': { ru: 'Принять {amount}', en: 'Take {amount}' },
  'payment.unpaid': { ru: 'Долг {amount}', en: '{amount} owed' },
  'neworder.payLater': { ru: 'Позже', en: 'Later' },
  'neworder.completeUnpaid': { ru: 'Записать без оплаты', en: 'Record unpaid' },

  'invoice.printIn': { ru: 'Чек на языке', en: 'Receipt in' },
  'invoice.title': { ru: 'СЧЁТ', en: 'Invoice' },
  'invoice.customer': { ru: 'Клиент', en: 'Customer' },
  'invoice.vehicle': { ru: 'Машина', en: 'Vehicle' },
  'invoice.servedBy': { ru: 'Обслужил {name}', en: 'Served by {name}' },
  'invoice.service': { ru: 'Услуга', en: 'Service' },
  'invoice.qty': { ru: 'Кол-во', en: 'Qty' },
  'invoice.price': { ru: 'Цена', en: 'Price' },
  'invoice.total': { ru: 'Сумма', en: 'Total' },
  'invoice.subtotal': { ru: 'Итого по услугам', en: 'Subtotal' },
  'invoice.discount': { ru: 'Скидка {percent}%', en: 'Discount {percent}%' },
  'invoice.cashbackApplied': { ru: 'Списано кэшбэка', en: 'Cashback applied' },
  'invoice.toPay': { ru: 'К оплате', en: 'To pay' },
  'invoice.paidBy': { ru: 'Оплата', en: 'Paid by' },
  'invoice.cashbackEarned': { ru: 'Начислено кэшбэка', en: 'Cashback earned' },
  'invoice.matures': { ru: '(созреет позже)', en: '(matures later)' },
  'invoice.points': { ru: 'Баллы', en: 'Points' },
  'invoice.balanceAfter': { ru: 'Баланс после:', en: 'Balance after:' },
  'invoice.thanks': { ru: 'Спасибо. {company}', en: 'Thank you. {company}' },
  'invoice.cash': { ru: 'Наличные', en: 'Cash' },
  'invoice.card': { ru: 'Карта', en: 'Card' },
  'invoice.transfer': { ru: 'Перевод', en: 'Bank transfer' },
  'invoice.other': { ru: 'Другое', en: 'Other' },

  // ---- manual adjustment ----------------------------------------------------
  'adjust.title': { ru: 'Ручная корректировка', en: 'Manual adjustment' },
  'adjust.intro': { ru: 'Каждая правка пишется в журнал с вашим именем.', en: 'Every change is logged under your name.' },
  'adjust.cashback': { ru: 'Кэшбэк', en: 'Cashback' },
  'adjust.points': { ru: 'Баллы', en: 'Points' },
  'adjust.pastSpend': { ru: 'Прошлые траты', en: 'Past spend' },
  'adjust.after': { ru: 'Станет', en: 'After' },
  'adjust.reasonRequired': { ru: 'Обязательно. Пишется в журнал.', en: 'Required. Recorded in the audit log.' },
  'adjust.record': { ru: 'Записать', en: 'Record' },
  'adjust.reasonPlaceholder': { ru: 'почему делается корректировка', en: 'why this is being adjusted' },

  // ---- vehicles on a customer -----------------------------------------------
  'veh.title': { ru: 'Машины', en: 'Vehicles' },
  'veh.add': { ru: '+ Машина', en: '+ Vehicle' },
  'veh.addButton': { ru: 'Добавить машину', en: 'Add vehicle' },
  'veh.empty': { ru: 'Машин пока нет.', en: 'No vehicles yet.' },
  'veh.make': { ru: 'Марка', en: 'Make' },
  'veh.model': { ru: 'Модель', en: 'Model' },
  'veh.year': { ru: 'Год', en: 'Year' },
  'veh.colour': { ru: 'Цвет', en: 'Colour' },
  'veh.colourPlaceholder': { ru: 'Чёрный', en: 'Black' },
  'veh.plate': { ru: 'Госномер', en: 'Plate' },
  'veh.vin': { ru: 'VIN', en: 'VIN' },
  'veh.optional': { ru: 'необязательно', en: 'optional' },
  'veh.keep': { ru: 'Оставить', en: 'Keep' },
  'veh.archive': { ru: 'В архив', en: 'Archive' },

  // ---- wallet card on a customer --------------------------------------------
  'walletIssue.title': { ru: 'Карта в телефоне', en: 'Wallet card' },
  'walletIssue.none': { ru: 'Карты на телефоне клиента ещё нет.', en: 'No card on this customer’s phone yet.' },
  'walletIssue.issue': { ru: 'Выпустить {provider}', en: 'Issue {provider}' },
  'walletIssue.retry': { ru: 'Повторить {provider}', en: 'Retry {provider}' },
  'walletIssue.notIssued': { ru: 'ещё не выпущена', en: 'not issued yet' },

  // ---- audit ----------------------------------------------------------------
  'audit.title': { ru: 'Журнал действий', en: 'Audit log' },
  'audit.empty': { ru: 'Пока ничего не записано', en: 'Nothing recorded yet' },

  // ---- import ---------------------------------------------------------------
  'import.title': { ru: 'Импорт клиентов', en: 'Import customers' },
  'import.upload': { ru: 'Загрузить CSV', en: 'Upload a CSV' },
  'import.template': { ru: 'Шаблон', en: 'Template' },
  'import.past': { ru: 'Прошлые импорты', en: 'Past imports' },
  'import.empty': { ru: 'Импортов ещё не было', en: 'Nothing imported yet' },
  'import.writing': { ru: 'Записываем клиентов…', en: 'Writing customers…' },

  // ---- reports --------------------------------------------------------------
  'reports.title': { ru: 'Отчёты', en: 'Reports' },
  'reports.from': { ru: 'С', en: 'From' },
  'reports.to': { ru: 'По', en: 'To' },
  'reports.days7': { ru: '7 дней', en: '7 days' },
  'reports.days30': { ru: '30 дней', en: '30 days' },
  'reports.days90': { ru: '90 дней', en: '90 days' },
  'reports.thisYear': { ru: 'Этот год', en: 'This year' },
  'reports.revenue': { ru: 'Выручка', en: 'Revenue' },
  'reports.revenueAfterDiscount': {
    ru: '{count|заказ|заказа|заказов}, за вычетом скидки {amount}',
    en: '{count} orders, after {amount} discount',
  },
  'reports.averageOrder': { ru: 'Средний чек', en: 'Average order' },
  'reports.customersCount': { ru: '{count|клиент|клиента|клиентов}', en: '{count|customer|customers}' },
  'reports.cashbackGiven': { ru: 'Начислено кэшбэка', en: 'Cashback given' },
  'reports.cashbackGivenSub': { ru: 'по этим заказам', en: 'earned on these orders' },
  'reports.pointsIssued': { ru: 'Начислено баллов', en: 'Points issued' },
  'reports.cashbackSpent': { ru: '{amount} кэшбэка списано', en: '{amount} cashback spent' },
  'reports.staffEarnings': { ru: 'Заработали мастера', en: 'What the team earned' },
  'reports.jobsAndCars': {
    ru: '{jobs|работа|работы|работ} · {cars|машина|машины|машин}',
    en: '{jobs|job|jobs} · {cars|car|cars}',
  },
  'reports.lessRefunded': { ru: '· вернули {amount}', en: '· {amount} refunded' },
  'reports.payLine': { ru: 'зарплата {amount}', en: 'pay {amount}' },
  'reports.payTotal': { ru: '· зарплаты {amount}', en: '· pay {amount}' },
  'reports.earningsTotal': {
    ru: 'Всего {amount} у {people|человека|человек|человек}',
    en: '{amount} across {people|person|people}',
  },
  'reports.noWork': { ru: 'За этот период работ не было', en: 'No work in this period' },
  'reports.topServices': { ru: 'Популярные услуги', en: 'Top services' },
  'reports.topCustomers': { ru: 'Лучшие клиенты', en: 'Top customers' },
  'reports.noSales': { ru: 'За этот период ничего не продано', en: 'Nothing sold in this range' },
  'reports.noVisits': { ru: 'За этот период никто не приходил', en: 'Nobody visited in this range' },

  // ---- services -------------------------------------------------------------
  'services.new': { ru: 'Новая услуга', en: 'New service' },
  'services.add': { ru: 'Добавить услугу', en: 'Add the service' },
  'services.category': { ru: 'Раздел', en: 'Section' },
  'services.pickCategory': { ru: 'Выберите раздел', en: 'Pick a section' },
  'services.addCategory': { ru: 'Новый раздел', en: 'New section' },
  'services.newCategory': { ru: 'Новый раздел каталога', en: 'New section of the catalogue' },
  'services.categoryName': { ru: 'Название раздела', en: 'Section name' },
  'services.name': { ru: 'Название', en: 'Name' },
  'services.priceLabel': { ru: 'Цена от', en: 'Price from' },
  'services.followup': { ru: 'Напомнить через, дней', en: 'Remind after, days' },

  'services.title': { ru: 'Услуги', en: 'Services' },
  'services.startingPrice': { ru: 'Стартовая цена', en: 'Starting price' },
  'services.savePrice': { ru: 'Сохранить цену', en: 'Save price' },
  'services.staffPay': { ru: 'Мастеру за одну, ₾', en: 'Technician per unit, ₾' },
  'services.staffPayNone': { ru: 'не задано', en: 'not set' },
  'services.staffPayShort': { ru: 'мастеру {amount}', en: 'technician {amount}' },
  'services.earns': { ru: 'Начисляет', en: 'Earns' },
  'services.followupAfter': { ru: 'напомнить через {days|день|дня|дней}', en: 'suggest again after {days} days' },
  'services.noFollowup': { ru: 'напоминание не настроено', en: 'no follow-up set' },
  'services.priceFrom': { ru: 'от {price}', en: 'from {price}' },

  // ---- settings -------------------------------------------------------------
  'settings.title': { ru: 'Настройки', en: 'Settings' },
  'settings.pending': {
    ru: '{count|изменение|изменения|изменений} не сохранено',
    en: '{count|change|changes} not saved yet',
  },
  'settings.subtitle': { ru: 'Версия правил лояльности {version}.', en: 'Loyalty rules version {version}.' },
  'settings.showAdvanced': { ru: 'Показать продвинутые', en: 'Show advanced' },
  'settings.tiers': { ru: 'Уровни', en: 'Tiers' },
  'settings.fromLifetime': { ru: 'От (за всё время)', en: 'From (lifetime)' },
  'settings.cashbackPercent': { ru: 'Кэшбэк %', en: 'Cashback %' },
  'settings.pointsMultiplier': { ru: 'Множитель баллов', en: 'Points multiplier' },
  'settings.discard': { ru: 'Отменить', en: 'Discard' },
  'settings.versioned': { ru: 'ВЕРСИОНИРУЕТСЯ', en: 'VERSIONED' },
  'settings.permanent': { ru: 'НАВСЕГДА', en: 'PERMANENT' },
  'settings.tierSummary': {
    ru: 'от {threshold} · {rate} · {multiplier} баллов',
    en: 'from {threshold} · {rate} · {multiplier} points',
  },

  // ---- shared controls ------------------------------------------------------
  'adjust.done': { ru: 'Корректировка записана в книгу.', en: 'Adjustment recorded in the ledger.' },
  'password.currentRequired': { ru: 'Введите текущий пароль', en: 'Enter your current password' },
  'password.tooShort': { ru: 'Не короче 12 символов', en: 'Use at least 12 characters' },
  'password.repeatRequired': { ru: 'Повторите новый пароль', en: 'Repeat the new password' },
  'password.reused': {
    ru: 'Выберите пароль, которым здесь ещё не пользовались',
    en: 'Choose a password you have not used here before',
  },
  'customers.empty': { ru: 'Клиентов пока нет', en: 'No customers yet' },
  'orders.empty': { ru: 'Заказов пока нет', en: 'No orders yet' },
  'import.reading': { ru: 'Читаем файл…', en: 'Reading the file…' },
  'rewards.new': { ru: 'Новая награда', en: 'New reward' },
  'rewards.edit': { ru: 'Изменить награду', en: 'Edit reward' },
  'rewards.create': { ru: 'Создать награду', en: 'Create reward' },
  'services.hideFromTill': { ru: 'Убрать из кассы', en: 'Hide from the till' },
  'services.showInTill': { ru: 'Показывать в кассе', en: 'Show in the till' },
  'staff.codeLocked': {
    ru: 'Код нельзя менять после того, как на него сослались работы',
    en: 'The code cannot change once work refers to it',
  },
  'staff.savePosition': { ru: 'Сохранить должность', en: 'Save position' },
  'staff.createPosition': { ru: 'Создать должность', en: 'Create position' },
  'customer.neverVisited': { ru: 'ни разу не был', en: 'never visited' },
  'services.nonePublished': { ru: 'не опубликована', en: 'none published' },
  'neworder.managerApproval': {
    ru: 'выше порога нужно одобрение руководителя',
    en: 'manager approval above the threshold',
  },
  'ui.decrease': { ru: 'Меньше', en: 'Decrease' },
  'ui.increase': { ru: 'Больше', en: 'Increase' },

  // ---- settings, by key -----------------------------------------------------
  'setting.loyalty.earning_basis': { ru: 'База начисления', en: 'Earning basis' },
  'setting.loyalty.discount_reduces_earning': { ru: 'Скидка уменьшает начисление', en: 'Discount reduces earning' },
  'setting.loyalty.allocation_policy': { ru: 'Как распределять списание', en: 'Allocation policy' },
  'setting.loyalty.tier_timing': { ru: 'Когда применяется уровень', en: 'When the tier applies' },
  'setting.loyalty.service_overrides_enabled': { ru: 'Свои ставки у услуг', en: 'Per-service rate overrides' },
  'setting.loyalty.min_order_for_cashback_tetri': { ru: 'Минимум заказа для кэшбэка', en: 'Minimum order for cashback' },
  'setting.loyalty.min_order_for_points_tetri': { ru: 'Минимум заказа для баллов', en: 'Minimum order for points' },
  'setting.loyalty.max_redemption_pct_bp': { ru: 'Максимум списания, %', en: 'Maximum redemption %' },
  'setting.loyalty.redemption_cap_basis': { ru: 'От чего считать потолок списания', en: 'Redemption cap basis' },
  'setting.loyalty.max_redemption_absolute_tetri': { ru: 'Абсолютный потолок списания', en: 'Absolute redemption cap' },
  'setting.loyalty.min_redemption_tetri': { ru: 'Минимальная сумма списания', en: 'Minimum redemption' },
  'setting.loyalty.allow_pending_redemption': { ru: 'Списывать созревающий кэшбэк', en: 'Allow redeeming pending cashback' },
  'setting.loyalty.pending_cashback_enabled': { ru: 'Кэшбэк созревает не сразу', en: 'Cashback matures before use' },
  'setting.loyalty.pending_cashback_days': { ru: 'Дней до созревания', en: 'Days until cashback matures' },
  'setting.loyalty.pending_cashback_min_order_tetri': { ru: 'С какой суммы заказа созревание', en: 'Order above which cashback matures' },
  'setting.loyalty.cashback_rounding': { ru: 'Округление кэшбэка', en: 'Cashback rounding' },
  'setting.loyalty.points_rounding': { ru: 'Округление баллов', en: 'Points rounding' },
  'setting.loyalty.redemption_cap_rounding': { ru: 'Округление потолка списания', en: 'Redemption cap rounding' },
  'setting.loyalty.spend_basis': { ru: 'База накопленных трат', en: 'Lifetime spend basis' },
  'setting.loyalty.blocked_customer_spend_policy': { ru: 'Траты заблокированного клиента', en: 'Blocked customer spend' },
  'setting.loyalty.allow_negative_cashback_balance': { ru: 'Разрешить отрицательный кэшбэк', en: 'Allow negative cashback balance' },
  'setting.loyalty.allow_negative_points_balance': { ru: 'Разрешить отрицательные баллы', en: 'Allow negative points balance' },
  'setting.loyalty.cashback_expiration_days': { ru: 'Срок жизни кэшбэка, дней', en: 'Cashback expires after days' },
  'setting.loyalty.points_expiration_days': { ru: 'Срок жизни баллов, дней', en: 'Points expire after days' },
  'setting.orders.reference_prefix': { ru: 'Префикс номера заказа', en: 'Order reference prefix' },
  'setting.orders.require_vehicle': { ru: 'Машина обязательна в заказе', en: 'Require a vehicle on an order' },
  'setting.inspection.intake_required': { ru: 'Фото-осмотр при приёмке обязателен', en: 'Photo inspection required at intake' },
  'setting.inspection.handover_required': { ru: 'Фото-осмотр при выдаче обязателен', en: 'Photo inspection required at handover' },
  'setting.orders.discount_approval_threshold_pct_bp': { ru: 'Скидка выше этого — с одобрения, %', en: 'Discount needing approval, %' },
  'setting.orders.large_order_threshold_tetri': { ru: 'Порог крупного заказа', en: 'Large order threshold' },
  'setting.loyalty.adjustment_manager_threshold_tetri': { ru: 'Порог корректировки для менеджера', en: 'Manager adjustment threshold' },
  'setting.crm.inactive_days_warning': { ru: 'Дней до пометки «давно не был»', en: 'Days before a customer looks quiet' },
  'setting.crm.inactive_days_lapsed': { ru: 'Дней до пометки «ушёл»', en: 'Days before a customer counts as lapsed' },
  'setting.crm.inactive_days_lost': { ru: 'Дней до пометки «потерян»', en: 'Days before a customer counts as lost' },
  'setting.crm.high_value_lifetime_tetri': { ru: 'Порог ценного клиента', en: 'High-value customer threshold' },
  'setting.customers.loyalty_id_prefix': { ru: 'Префикс номера карты', en: 'Loyalty ID prefix' },
  'setting.customers.access_link_ttl_minutes': { ru: 'Срок ссылки для входа, минут', en: 'Sign-in link lifetime, minutes' },
  'setting.rewards.code_prefix': { ru: 'Префикс кода награды', en: 'Reward code prefix' },
  'setting.security.admin_session_idle_days': { ru: 'Выход сотрудника после простоя, дней', en: 'Staff signed out after days unused' },
  'setting.security.customer_session_ttl_minutes': { ru: 'Срок сессии клиента, минут', en: 'Customer session lifetime, minutes' },
  'setting.security.admin_max_failed_logins': { ru: 'Неудачных входов до блокировки', en: 'Failed sign-ins before lockout' },
  'setting.security.admin_lockout_minutes': { ru: 'Длительность блокировки, минут', en: 'Lockout duration, minutes' },
  'setting.security.require_2fa_for_privileged_roles': { ru: 'Код при входе для владельца и админов', en: 'Code at sign-in for owners and admins' },
  'setting.otp.code_length': { ru: 'Длина кода', en: 'Passcode length' },
  'setting.otp.ttl_seconds': { ru: 'Срок действия кода, секунд', en: 'Passcode lifetime, seconds' },
  'setting.otp.max_attempts': { ru: 'Попыток ввода кода', en: 'Passcode attempts allowed' },
  'setting.otp.resend_cooldown_seconds': { ru: 'Пауза перед повторной отправкой, секунд', en: 'Resend cooldown, seconds' },
  'setting.otp.max_per_phone_per_hour': { ru: 'Кодов на номер в час', en: 'Passcodes per phone per hour' },
  'setting.otp.max_per_ip_per_hour': { ru: 'Кодов на адрес в час', en: 'Passcodes per address per hour' },
  'setting.qr.portal_token_ttl_seconds': { ru: 'Срок QR-кода в кабинете, секунд', en: 'Portal QR lifetime, seconds' },
  'setting.qr.resolve_rate_per_minute': { ru: 'Распознаваний QR в минуту', en: 'QR scans per minute' },
  'setting.referrals.referrer_cashback_tetri': { ru: 'Кэшбэк пригласившему', en: 'Cashback to the referrer' },
  'setting.referrals.referrer_points': { ru: 'Баллы пригласившему', en: 'Points to the referrer' },
  'setting.referrals.referee_cashback_tetri': { ru: 'Кэшбэк приглашённому', en: 'Cashback to the invitee' },
  'setting.referrals.referee_points': { ru: 'Баллы приглашённому', en: 'Points to the invitee' },
  'setting.referrals.min_first_order_tetri': { ru: 'Минимум первого заказа для бонуса', en: 'Minimum first order for the bonus' },
  'setting.wallet.apple_pass_type_id': { ru: 'Apple Pass Type ID', en: 'Apple Pass Type ID' },
  'setting.wallet.google_class_id': { ru: 'Google Wallet Class ID', en: 'Google Wallet Class ID' },
  'setting.wallet.organization_name': { ru: 'Название студии на карте', en: 'Organisation name on the card' },
  'setting.features.apple_wallet_enabled': { ru: 'Apple Wallet включён', en: 'Apple Wallet enabled' },
  'setting.features.google_wallet_enabled': { ru: 'Google Wallet включён', en: 'Google Wallet enabled' },
  'setting.features.sms_enabled': { ru: 'SMS включены', en: 'SMS enabled' },
  'setting.features.email_enabled': { ru: 'Почта включена', en: 'Email enabled' },
  'setting.features.rewards_enabled': { ru: 'Награды включены', en: 'Rewards enabled' },
  'setting.features.referrals_enabled': { ru: 'Приглашения включены', en: 'Referrals enabled' },
  'setting.company.name': { ru: 'Название компании', en: 'Company name' },
  'setting.company.timezone': { ru: 'Часовой пояс', en: 'Time zone' },
  'setting.company.default_language': { ru: 'Язык по умолчанию', en: 'Default language' },
  'setting.company.phone': { ru: 'Телефон', en: 'Phone' },
  'setting.company.support_email': { ru: 'Почта для связи', en: 'Support email' },
  'setting.company.address': { ru: 'Адрес', en: 'Address' },
  'settingCat.EARNING': { ru: 'Начисление', en: 'Earning' },
  'settingCat.REDEMPTION': { ru: 'Списание', en: 'Redemption' },
  'settingCat.PENDING': { ru: 'Созревание кэшбэка', en: 'Pending cashback' },
  'settingCat.ROUNDING': { ru: 'Округление', en: 'Rounding' },
  'settingCat.SPEND': { ru: 'Накопленные траты, долг и сгорание', en: 'Lifetime spend, debt and expiry' },
  'settingCat.ORDERS': { ru: 'Заказы и пороги одобрения', en: 'Orders and approval thresholds' },
  'settingCat.CRM': { ru: 'Работа с клиентами', en: 'CRM' },
  'settingCat.SECURITY': { ru: 'Безопасность', en: 'Security' },
  'settingCat.OTP': { ru: 'Одноразовые коды', en: 'One-time passcodes' },
  'settingCat.NOTIFICATIONS': { ru: 'Уведомления', en: 'Notifications' },
  'settingCat.WALLET': { ru: 'Карты в телефоне', en: 'Wallet' },
  'settingCat.COMPANY': { ru: 'Компания', en: 'Company' },
  'settingCat.FEATURES': { ru: 'Переключатели функций', en: 'Feature flags' },

  // ---- permissions and roles, translated by code ----------------------------
  'perm.customers.read': { ru: 'Просмотр клиентов и их истории', en: 'View customers and their history' },
  'perm.customers.create': { ru: 'Заведение клиентов', en: 'Create customers' },
  'perm.customers.update': { ru: 'Изменение данных клиента', en: 'Edit customer details' },
  'perm.customers.block': { ru: 'Блокировка и разблокировка клиентов', en: 'Block and unblock customers' },
  'perm.customers.merge': { ru: 'Объединение дубликатов', en: 'Merge duplicate customers' },
  'perm.customers.export': { ru: 'Выгрузка персональных данных клиента', en: 'Export a customer’s personal data' },
  'perm.customers.unmask': { ru: 'Просмотр телефонов и почты целиком', en: 'See unmasked phone numbers and email addresses' },
  'perm.customers.delete': { ru: 'Полное удаление клиента и всей его истории', en: 'Permanently delete a customer and all their history' },
  'perm.vehicles.read': { ru: 'Просмотр машин', en: 'View vehicles' },
  'perm.vehicles.create': { ru: 'Добавление машин', en: 'Add vehicles' },
  'perm.vehicles.update': { ru: 'Изменение и архивирование машин', en: 'Edit and archive vehicles' },
  'perm.orders.read': { ru: 'Просмотр заказов', en: 'View orders' },
  'perm.orders.create': { ru: 'Создание черновиков заказов', en: 'Create draft orders' },
  'perm.orders.set_price': { ru: 'Ввод фактической цены услуги', en: 'Enter the price actually charged for a service' },
  'perm.orders.complete': { ru: 'Проведение заказа и запись в книги', en: 'Complete an order and post it to the ledgers' },
  'perm.orders.discount': { ru: 'Скидка в пределах порога', en: 'Grant a discount within the approval threshold' },
  'perm.orders.discount.large': { ru: 'Скидка выше порога', en: 'Grant a discount above the approval threshold' },
  'perm.orders.cancel': { ru: 'Отмена черновика или подтверждённого заказа', en: 'Cancel a draft or confirmed order' },
  'perm.orders.refund': { ru: 'Возврат по проведённому заказу', en: 'Refund a completed order' },
  'perm.loyalty.read': { ru: 'Просмотр балансов и книг записей', en: 'View loyalty balances and ledgers' },
  'perm.loyalty.redeem': { ru: 'Списание кэшбэка в заказе', en: 'Apply cashback to an order' },
  'perm.loyalty.adjust': { ru: 'Корректировка кэшбэка и баллов в пределах порога', en: 'Adjust cashback or points within the threshold' },
  'perm.loyalty.adjust.large': { ru: 'Корректировка кэшбэка и баллов выше порога', en: 'Adjust cashback or points above the threshold' },
  'perm.loyalty.legacy_import': { ru: 'Запись прошлых трат существующему клиенту', en: 'Record historical spend for an existing customer' },
  'perm.loyalty.block_override': { ru: 'Начисление и списание заблокированному клиенту', en: 'Let a blocked customer earn or redeem' },
  'perm.loyalty.configure': { ru: 'Изменение уровней, ставок и правил лояльности', en: 'Edit tiers, rates and loyalty rules' },
  'perm.rewards.read': { ru: 'Просмотр каталога наград', en: 'View the reward catalog' },
  'perm.rewards.create': { ru: 'Создание наград', en: 'Create rewards' },
  'perm.rewards.update': { ru: 'Изменение и архивирование наград', en: 'Edit and archive rewards' },
  'perm.rewards.redeem': { ru: 'Погашение кода награды', en: 'Mark a reward code as used' },
  'perm.services.read': { ru: 'Просмотр каталога услуг', en: 'View the service catalog' },
  'perm.services.create': { ru: 'Создание услуг', en: 'Create services' },
  'perm.services.update': { ru: 'Изменение услуг и стартовых цен', en: 'Edit service details and starting prices' },
  'perm.wallet.read': { ru: 'Просмотр состояния карт в телефоне', en: 'View wallet pass status' },
  'perm.wallet.issue': { ru: 'Выпуск и синхронизация карт', en: 'Issue and re-sync wallet passes' },
  'perm.wallet.revoke': { ru: 'Отзыв карты', en: 'Revoke a wallet pass' },
  'perm.qr.resolve': { ru: 'Распознавание QR-кода клиента', en: 'Resolve a scanned customer QR code' },
  'perm.reports.read': { ru: 'Просмотр отчётов', en: 'View reports' },
  'perm.reports.export': { ru: 'Выгрузка отчётов в CSV', en: 'Export reports to CSV' },
  'perm.imports.read': { ru: 'Просмотр партий импорта', en: 'View import batches' },
  'perm.imports.create': { ru: 'Загрузка и предпросмотр импорта', en: 'Upload and preview a customer import' },
  'perm.imports.confirm': { ru: 'Подтверждение импорта и запись записей', en: 'Confirm an import and write the records' },
  'perm.staff.read': { ru: 'Просмотр учётных записей сотрудников', en: 'View staff accounts' },
  'perm.staff.create': { ru: 'Создание учётных записей', en: 'Create staff accounts' },
  'perm.staff.update': { ru: 'Изменение учётных записей, ролей и сессий', en: 'Edit staff accounts, roles and sessions' },
  'perm.settings.read': { ru: 'Просмотр настроек', en: 'View settings' },
  'perm.settings.update': { ru: 'Изменение настроек бизнеса', en: 'Change business settings' },
  'perm.settings.security': { ru: 'Изменение настроек безопасности', en: 'Change security settings' },
  'perm.audit.read': { ru: 'Чтение журнала действий', en: 'Read the audit log' },
  'role.OWNER': { ru: 'Владелец', en: 'Owner' },
  'role.ADMIN': { ru: 'Администратор', en: 'Admin' },
  'role.MANAGER': { ru: 'Менеджер', en: 'Manager' },
  'role.CASHIER': { ru: 'Кассир', en: 'Cashier' },
  'role.STAFF': { ru: 'Сотрудник', en: 'Staff' },

  // ---- errors from the API --------------------------------------------------
  // Keyed by the stable code the server returns, not by its English sentence.
  // The server stays monolingual; whoever reads the screen does not have to.
  'error.UNAUTHENTICATED': { ru: 'Войдите, чтобы продолжить', en: 'Sign in to continue' },
  'error.INVALID_CREDENTIALS': { ru: 'Неверная почта или пароль', en: 'Email or password is incorrect' },
  'error.SESSION_EXPIRED': { ru: 'Сессия истекла, войдите заново', en: 'Your session has expired' },
  'error.FORBIDDEN': { ru: 'Недостаточно прав для этого действия', en: 'You do not have permission for this' },
  'error.PASSWORD_CHANGE_REQUIRED': { ru: 'Сначала смените пароль', en: 'Change your password first' },
  'error.TWO_FACTOR_REQUIRED': { ru: 'Введите код из приложения', en: 'Enter the code from your app' },
  'error.ORDER_NOT_OPEN': {
    ru: 'Эта машина уже уехала — на площадке её нет',
    en: 'That car has already left the floor',
  },
  'error.INSPECTION_INCOMPLETE': {
    ru: 'Сняты не все обязательные фото машины',
    en: 'Not every required photo of the car has been taken',
  },
  'error.INSPECTION_REQUIRED': {
    ru: 'Сначала сфотографируйте машину со всех сторон, VIN и приборку',
    en: 'Photograph the car from every side, its VIN and dashboard first',
  },
  'error.TWO_FACTOR_SETUP_REQUIRED': {
    ru: 'Сначала настройте вход по коду',
    en: 'Set up the code at sign-in first',
  },
  'error.ACCOUNT_LOCKED': {
    ru: 'Вход временно заблокирован из-за неудачных попыток',
    en: 'Sign-in is locked after too many failed attempts',
  },
  'error.OTP_INVALID': { ru: 'Код неверен', en: 'That code is not valid' },
  'error.OTP_EXPIRED': { ru: 'Срок действия кода истёк', en: 'That code has expired' },
  'error.OTP_TOO_MANY_ATTEMPTS': { ru: 'Слишком много попыток ввода кода', en: 'Too many attempts' },
  'error.OTP_RATE_LIMITED': { ru: 'Слишком часто. Подождите немного.', en: 'Too often. Wait a little.' },
  'error.VALIDATION_FAILED': { ru: 'Проверьте заполненные поля', en: 'Some fields are invalid' },
  'error.NOT_FOUND': { ru: 'Не найдено', en: 'Not found' },
  'error.CONFLICT': { ru: 'Это действие сейчас невозможно', en: 'That cannot be done right now' },
  'error.VEHICLE_HAS_ORDERS': { ru: 'По этой машине есть заказы — сначала удалите их', en: 'This car has orders — delete them first' },
  'error.REWARD_HAS_REDEMPTIONS': { ru: 'Эту награду уже обменивали — её можно только убрать из каталога', en: 'This reward has been redeemed — it can only be hidden' },
  'erase.action': { ru: 'Удалить навсегда', en: 'Delete for good' },
  'erase.question': { ru: 'Удалить навсегда?', en: 'Delete for good?' },
  'erase.confirm': { ru: 'Удалить', en: 'Delete' },
  'erase.cashback': { ru: 'Кэшбэк клиента', en: 'Customer cashback' },
  'erase.points': { ru: 'Баллы', en: 'Points' },
  'erase.spend': { ru: 'Траты', en: 'Spend' },
  'error.DUPLICATE_PHONE': { ru: 'Клиент с таким телефоном уже есть', en: 'A customer with this phone already exists' },
  'error.INSUFFICIENT_CASHBACK': { ru: 'Не хватает кэшбэка', en: 'Not enough cashback' },
  'error.INSUFFICIENT_POINTS': { ru: 'Не хватает баллов', en: 'Not enough points' },
  'error.REDEMPTION_EXCEEDS_LIMIT': { ru: 'Больше, чем разрешено списать', en: 'More than the redemption limit allows' },
  'error.CUSTOMER_BLOCKED': { ru: 'Клиент заблокирован', en: 'That customer is blocked' },
  'error.ORDER_ALREADY_COMPLETED': { ru: 'Заказ уже проведён', en: 'That order is already completed' },
  'error.ORDER_NOT_COMPLETABLE': { ru: 'Заказ нельзя провести', en: 'That order cannot be completed' },
  'error.REFUND_EXCEEDS_ORDER': { ru: 'Возврат больше суммы заказа', en: 'The refund exceeds the order' },
  'error.BALANCE_CHANGED': { ru: 'Баланс изменился, откройте заново', en: 'The balance changed; reload and try again' },
  'error.REWARD_UNAVAILABLE': { ru: 'Награда недоступна', en: 'That reward is not available' },
  'error.REWARD_ALREADY_USED': { ru: 'Награда уже использована', en: 'That reward has already been used' },
  'error.IDEMPOTENCY_KEY_REQUIRED': { ru: 'Повторите действие', en: 'Please retry the action' },
  'error.IDEMPOTENCY_KEY_REUSED': { ru: 'Это действие уже выполнено', en: 'That action was already carried out' },
  'error.REQUEST_IN_PROGRESS': { ru: 'Действие уже выполняется', en: 'That action is already running' },
  'error.RATE_LIMITED': { ru: 'Слишком много запросов. Подождите.', en: 'Too many requests. Please slow down.' },
  'error.SERVICE_UNAVAILABLE': { ru: 'Сервис временно недоступен', en: 'The service is temporarily unavailable' },
  'error.INTERNAL_ERROR': { ru: 'Что-то пошло не так на нашей стороне', en: 'Something went wrong on our side' },

  // ---- codes the database hands us -----------------------------------------
  // Order, redemption, import, wallet and actor codes, plus payment methods and
  // languages. Read through useCodeLabel, which falls back to the raw code.
  'status.DRAFT': { ru: 'черновик', en: 'draft' },
  'status.CONFIRMED': { ru: 'подтверждён', en: 'confirmed' },
  'status.IN_PROGRESS': { ru: 'в работе', en: 'in progress' },
  'status.COMPLETED': { ru: 'проведён', en: 'completed' },
  'status.CANCELLED': { ru: 'отменён', en: 'cancelled' },
  'status.REFUNDED': { ru: 'возвращён', en: 'refunded' },
  'status.PARTIALLY_REFUNDED': { ru: 'частичный возврат', en: 'partially refunded' },
  'status.ISSUED': { ru: 'выдан', en: 'issued' },
  'status.USED': { ru: 'использован', en: 'used' },
  'status.EXPIRED': { ru: 'истёк', en: 'expired' },
  'status.UPLOADED': { ru: 'загружен', en: 'uploaded' },
  'status.PREVIEWED': { ru: 'проверен', en: 'previewed' },
  'status.PROCESSING': { ru: 'обрабатывается', en: 'processing' },
  'status.FAILED': { ru: 'не удалось', en: 'failed' },
  'status.VALID': { ru: 'годная', en: 'valid' },
  'status.DUPLICATE': { ru: 'дубль', en: 'duplicate' },
  'status.INVALID': { ru: 'с ошибкой', en: 'invalid' },
  'status.IMPORTED': { ru: 'загружена', en: 'imported' },
  'status.SKIPPED': { ru: 'пропущена', en: 'skipped' },
  'status.ACTIVE': { ru: 'активна', en: 'active' },
  'status.NOT_ISSUED': { ru: 'не выпущена', en: 'not issued' },
  'status.ERROR': { ru: 'ошибка', en: 'error' },
  'status.REVOKED': { ru: 'отозвана', en: 'revoked' },
  'statusShort.REFUNDED': { ru: 'ВОЗВРАТ', en: 'REFUNDED' },
  'statusShort.PARTIALLY_REFUNDED': { ru: 'ЧАСТ. ВОЗВРАТ', en: 'PART. REFUNDED' },

  'method.CASH': { ru: 'наличные', en: 'cash' },
  'method.CARD': { ru: 'карта', en: 'card' },
  'method.BANK_TRANSFER': { ru: 'перевод', en: 'bank transfer' },
  'method.CASHBACK': { ru: 'кэшбэк', en: 'cashback' },
  'method.OTHER': { ru: 'другое', en: 'other' },

  'actor.ADMIN': { ru: 'СОТРУДНИК', en: 'STAFF' },
  'actor.CUSTOMER': { ru: 'КЛИЕНТ', en: 'CUSTOMER' },
  'actor.SYSTEM': { ru: 'СИСТЕМА', en: 'SYSTEM' },

  'lang.ka': { ru: 'грузинский', en: 'Georgian' },
  'lang.ru': { ru: 'русский', en: 'Russian' },
  'lang.en': { ru: 'английский', en: 'English' },

  // ---- the last English strings on the screens ------------------------------
  'common.copy': { ru: 'Скопировать', en: 'Copy' },
  'common.copied': { ru: 'Скопировано', en: 'Copied' },
  'common.open': { ru: 'открыть', en: 'open' },
  'common.hide': { ru: 'Скрыть', en: 'Hide' },
  'common.unhide': { ru: 'Показывать', en: 'Show' },
  'common.pts': { ru: 'балл.', en: 'pts' },
  'common.system': { ru: 'система', en: 'system' },
  'common.deactivate': { ru: 'Отключить', en: 'Deactivate' },
  'common.reactivate': { ru: 'Включить', en: 'Reactivate' },

  'app.apiUnreachable': { ru: 'Не удалось связаться с сервером.', en: 'The API could not be reached.' },
  'app.requestId': { ru: 'Запрос {id}', en: 'Request {id}' },

  'password.setNew': { ru: 'Задайте новый пароль', en: 'Set a new password' },

  'access.intro': { ru: 'Выпустите ссылку, если код из СМС не доходит.', en: 'Issue a link if the SMS code does not arrive.' },
  'access.qrAlt': { ru: 'Ссылка для входа', en: 'Sign-in link' },
  'access.expired': { ru: 'срок истёк — выпустите новую', en: 'expired — issue a new one' },
  'access.validFor': { ru: 'действует ещё {time}', en: 'valid for {time}' },

  'adjust.amount': { ru: 'Сумма', en: 'Amount' },
  'adjust.pastSpendNote': { ru: 'Учитывает траты до этой системы. Кэшбэк и баллы не начисляются.', en: 'Counts spending from before this system. No cashback or points.' },

  'walletIssue.synced': { ru: 'обновлена {when}', en: 'synced {when}' },


  'ui.qty': { ru: 'Кол-во', en: 'Qty' },

  'audit.filterAll': { ru: 'Всё', en: 'Everything' },
  'audit.filterOrders': { ru: 'Заказы', en: 'Orders' },
  'audit.filterCustomers': { ru: 'Клиенты', en: 'Customers' },
  'audit.filterLoyalty': { ru: 'Лояльность', en: 'Loyalty' },
  'audit.filterSettings': { ru: 'Настройки', en: 'Settings' },
  'audit.filterBookings': { ru: 'Записи', en: 'Bookings' },
  'audit.filterSignIns': { ru: 'Входы', en: 'Sign-ins' },
  'audit.openCustomer': { ru: 'открыть клиента', en: 'open customer' },
  'audit.openOrder': { ru: 'открыть заказ', en: 'open order' },

  'customer.back': { ru: '← Клиенты', en: '← Customers' },
  'customer.heldPermanently': { ru: 'ЗАКРЕПЛЁН', en: 'HELD PERMANENTLY' },
  'customer.blockedPill': { ru: 'ЗАБЛОКИРОВАН', en: 'BLOCKED' },
  'customer.blockedWarning': {
    ru: 'Клиент заблокирован: начислять и списывать нельзя.',
    en: 'This customer is blocked and cannot earn or redeem.',
  },
  'customer.toTier': { ru: '{amount} до {tier}', en: '{amount} to {tier}' },
  'customer.topTierOf': { ru: '{code} · высший уровень', en: '{code} · top tier' },
  'customer.maturing': { ru: '{amount} созревает', en: '{amount} maturing' },
  'customer.cashbackSpentLine': { ru: '−{amount} кэшбэком · ', en: '−{amount} cashback · ' },

  'customers.searchTry': {
    ru: 'Попробуйте номер телефона, ID клиента или госномер.',
    en: 'Try a phone number, loyalty ID or plate.',
  },

  'order.back': { ru: '← Заказы', en: '← Orders' },
  'order.refundedTitle': { ru: 'Возвращено {amount}', en: 'Refunded {amount}' },
  'order.wholeOrder': { ru: ' — весь заказ', en: ' — the whole order' },
  'order.cashToHandBack': { ru: 'Выдать наличными: {amount}', en: 'Cash to hand back: {amount}' },
  'order.cashbackReturned': { ru: 'Кэшбэк возвращён клиенту: {amount}', en: 'Cashback returned: {amount}' },
  'order.cashbackReversed': { ru: 'Кэшбэк отменён: {amount}', en: 'Cashback reversed: {amount}' },
  'order.pointsReversed': { ru: 'Баллы отменены: {points}', en: 'Points reversed: {points}' },
  'order.forgiven': {
    ru: 'Прощено, иначе баланс ушёл бы в минус: {amount}',
    en: 'Forgiven (balance would have gone negative): {amount}',
  },
  'order.tierNow': { ru: 'Уровень теперь {tier}', en: 'Tier now {tier}' },
  'order.refundedQty': { ru: 'возвращено: {count}', en: '{count} refunded' },
  'order.refundBullet1': { ru: 'возвращается работ на {amount}', en: '{amount} of work is refunded' },
  'order.refundBullet2': {
    ru: 'кэшбэк и баллы по этому заказу отменяются пропорционально',
    en: 'the cashback and points this order granted are reversed pro-rata',
  },
  'order.refundBullet3': {
    ru: 'кэшбэк, который клиент на него потратил, возвращается ему',
    en: 'any cashback the customer spent on it is returned to them',
  },
  'order.refundBullet4': {
    ru: 'сумма за всё время уменьшается; закреплённый уровень сохраняется',
    en: 'lifetime spend is reduced; a permanent tier is kept',
  },
  'order.refundBullet5': { ru: 'отмены пишутся новыми записями', en: 'reversals are written as new entries' },

  'orders.filterAll': { ru: 'Все', en: 'All' },

  'services.hiddenPill': { ru: 'СКРЫТА', en: 'HIDDEN' },
  'services.noCashbackPill': { ru: 'БЕЗ КЭШБЭКА', en: 'NO CASHBACK' },
  'services.noPointsPill': { ru: 'БЕЗ БАЛЛОВ', en: 'NO POINTS' },
  'services.overridePill': { ru: '{rate} ОСОБАЯ СТАВКА', en: '{rate} OVERRIDE' },
  'services.cashback': { ru: 'Кэшбэк', en: 'Cashback' },
  'services.points': { ru: 'Баллы', en: 'Points' },

  'settings.saved': { ru: 'Сохранено.', en: 'Saved.' },
  'settings.advancedPill': { ru: 'ПРОДВИНУТАЯ', en: 'ADVANCED' },
  'settings.none': { ru: 'не задано', en: 'none' },
  'settings.rulesChanged': {
    ru: 'Версия правил {version}. Прошлые заказы считаются по старой.',
    en: 'Rules version {version}. Past orders keep the old one.',
  },

  'staff.lockedPill': { ru: 'ЗАБЛОКИРОВАН', en: 'LOCKED' },
  'staff.noLoginPill': { ru: 'БЕЗ ВХОДА', en: 'NO LOGIN' },
  'staff.inactivePill': { ru: 'ОТКЛЮЧЁН', en: 'INACTIVE' },
  'staff.codeHint': { ru: 'A–Z, цифры, подчёркивания', en: 'A–Z, digits, underscores' },

  'import.chooseFile': { ru: 'Выберите CSV-файл', en: 'Choose a CSV file' },
  'import.columnsHint': {
    ru: 'обязательны first_name и phone; остальное по желанию',
    en: 'first_name and phone are required; everything else is optional',
  },
  'import.confirmNote': { ru: 'Ничего не запишется до подтверждения. Кэшбэк не начисляется.', en: 'Nothing is written until you confirm. No cashback is granted.' },
  'import.rows': { ru: 'строк', en: 'rows' },
  'import.valid': { ru: 'годных', en: 'valid' },
  'import.duplicates': { ru: 'дублей', en: 'duplicates' },
  'import.invalid': { ru: 'с ошибкой', en: 'invalid' },
  'import.importedCount': { ru: 'Загружено {count|клиент|клиента|клиентов}', en: 'Imported {count|customer|customers}' },
  'import.failedCount': { ru: ', не прошло: {count}', en: ', {count} failed' },
  'import.importButton': { ru: 'Загрузить {count|клиента|клиентов|клиентов}', en: 'Import {count|customer|customers}' },
  'import.existing': { ru: 'уже есть', en: 'existing' },

  'neworder.nobodyMatches': { ru: 'По запросу «{query}» никого нет.', en: 'Nobody matches “{query}”.' },
  'neworder.addServiceShort': { ru: '+ Услуга', en: '+ Service' },

  'reports.ordersPlural': { ru: '{count|заказ|заказа|заказов}', en: '{count|order|orders}' },

  'rewards.use': { ru: 'Списать', en: 'Use' },
  'rewards.hiddenPill': { ru: 'СКРЫТА', en: 'HIDDEN' },
  'rewards.issuedCount': { ru: 'выдано: {count}', en: '{count} issued' },
  'rewards.validDays': { ru: 'срок — {days|день|дня|дней}', en: 'valid {days} days' },
  'rewards.maxPer': { ru: 'не более {count} на клиента', en: 'max {count} per customer' },
  'rewards.hide': { ru: 'Скрыть', en: 'Hide' },
  'rewards.show': { ru: 'Показывать', en: 'Show' },
  'rewards.outstanding': { ru: 'Не использованы', en: 'Outstanding' },
  'rewards.used': { ru: 'Использованы', en: 'Used' },

  // ---- audit actions --------------------------------------------------------
  // The machine code stays on screen under the label, so an entry can still be
  // matched against the database; this only puts words next to it.
  'auditAction.admin.login': { ru: 'Вход сотрудника', en: 'Staff signed in' },
  'auditAction.admin.login.failed': { ru: 'Неудачный вход', en: 'Failed sign-in' },
  'auditAction.admin.logout': { ru: 'Выход сотрудника', en: 'Staff signed out' },
  'auditAction.admin.password.changed': { ru: 'Пароль изменён', en: 'Password changed' },
  'auditAction.admin.session.revoked': { ru: 'Сеанс прерван', en: 'Session revoked' },
  'auditAction.customer.access_link.issued': { ru: 'Выдана ссылка для входа', en: 'Sign-in link issued' },
  'auditAction.customer.blocked': { ru: 'Клиент заблокирован', en: 'Customer blocked' },
  'auditAction.customer.created': { ru: 'Клиент заведён', en: 'Customer created' },
  'auditAction.customer.deleted': { ru: 'Клиент удалён насовсем', en: 'Customer permanently deleted' },
  'auditAction.customer.login.access_link': { ru: 'Клиент вошёл по ссылке', en: 'Customer signed in by link' },
  'auditAction.customer.login.otp': { ru: 'Клиент вошёл по коду', en: 'Customer signed in by code' },
  'auditAction.customer.merged': { ru: 'Дубликат присоединён', en: 'Duplicate merged' },
  'auditAction.customer.note.added': { ru: 'Добавлена заметка', en: 'Note added' },
  'auditAction.customer.otp.opted_in': { ru: 'Клиент включил коды', en: 'Customer opted into codes' },
  'auditAction.customer.otp.opted_out': { ru: 'Клиент отключил коды', en: 'Customer opted out of codes' },
  'auditAction.customer.qr.rotated': { ru: 'QR перевыпущен', en: 'QR rotated' },
  'auditAction.customer.unblocked': { ru: 'Клиент разблокирован', en: 'Customer unblocked' },
  'auditAction.customer.updated': { ru: 'Клиент изменён', en: 'Customer updated' },
  'auditAction.followup.dismissed': { ru: 'Напоминание закрыто', en: 'Follow-up dismissed' },
  'auditAction.import.confirmed': { ru: 'Импорт подтверждён', en: 'Import confirmed' },
  'auditAction.import.uploaded': { ru: 'Файл импорта загружен', en: 'Import uploaded' },
  'auditAction.loyalty.cashback.adjusted': { ru: 'Кэшбэк скорректирован', en: 'Cashback adjusted' },
  'auditAction.loyalty.legacy_import': { ru: 'Внесены прошлые траты', en: 'Past spend recorded' },
  'auditAction.loyalty.points.adjusted': { ru: 'Баллы скорректированы', en: 'Points adjusted' },
  'auditAction.loyalty.tier.changed': { ru: 'Уровень изменён', en: 'Tier changed' },
  'auditAction.order.accepted': { ru: 'Машина принята', en: 'Car taken in' },
  'auditAction.order.assigned': { ru: 'Работа назначена', en: 'Work assigned' },
  'auditAction.order.workAdded': { ru: 'Добавлена работа', en: 'Work added' },
  'auditAction.order.vehicleSet': { ru: 'Указана машина', en: 'Vehicle set' },
  'auditAction.order.staffPaySet': { ru: 'Изменена зарплата', en: 'Staff pay changed' },
  'auditAction.order.work': { ru: 'Ход работы', en: 'Work progress' },
  'auditAction.order.cancelled': { ru: 'Машина отменена', en: 'Car called off' },
  'auditAction.booking.created': { ru: 'Клиент записан', en: 'Booking made' },
  'auditAction.booking.updated': { ru: 'Запись изменена', en: 'Booking changed' },
  'auditAction.booking.cancelled': { ru: 'Запись отменена', en: 'Booking called off' },
  'auditAction.booking.noShow': { ru: 'Клиент не пришёл', en: 'Customer did not come' },
  'auditAction.admin.2fa.enabled': { ru: 'Код при входе включён', en: 'Sign-in code turned on' },
  'auditAction.admin.2fa.disabled': { ru: 'Код при входе выключен', en: 'Sign-in code turned off' },
  'auditAction.order.completed': { ru: 'Заказ проведён', en: 'Order completed' },
  'auditAction.order.refunded': { ru: 'Возврат по заказу', en: 'Order refunded' },
  'auditAction.qr.resolved': { ru: 'Карта отсканирована', en: 'Card scanned' },
  'auditAction.reward.archived': { ru: 'Награда удалена', en: 'Reward archived' },
  'auditAction.reward.created': { ru: 'Награда создана', en: 'Reward created' },
  'auditAction.reward.issued': { ru: 'Награда выдана', en: 'Reward issued' },
  'auditAction.reward.redeemed': { ru: 'Награда списана', en: 'Reward redeemed' },
  'auditAction.reward.updated': { ru: 'Награда изменена', en: 'Reward updated' },
  'auditAction.role.archived': { ru: 'Должность удалена', en: 'Position archived' },
  'auditAction.role.created': { ru: 'Должность создана', en: 'Position created' },
  'auditAction.role.updated': { ru: 'Должность изменена', en: 'Position updated' },
  'auditAction.service.updated': { ru: 'Услуга изменена', en: 'Service updated' },
  'auditAction.settings.changed': { ru: 'Настройки изменены', en: 'Settings changed' },
  'auditAction.staff.created': { ru: 'Сотрудник добавлен', en: 'Staff member added' },
  'auditAction.staff.updated': { ru: 'Сотрудник изменён', en: 'Staff member updated' },
  'auditAction.vehicle.archived': { ru: 'Машина в архиве', en: 'Vehicle archived' },
  'auditAction.vehicle.created': { ru: 'Машина добавлена', en: 'Vehicle added' },
  'auditAction.vehicle.updated': { ru: 'Машина изменена', en: 'Vehicle updated' },
  'auditAction.wallet.issue': { ru: 'Карта выпущена', en: 'Wallet pass issued' },
  'auditAction.wallet.revoke': { ru: 'Карта отозвана', en: 'Wallet pass revoked' },

  // ---- permission groups ----------------------------------------------------
  'permGroup.AUDIT': { ru: 'Журнал', en: 'Audit' },
  'permGroup.CUSTOMERS': { ru: 'Клиенты', en: 'Customers' },
  'permGroup.IMPORTS': { ru: 'Импорт', en: 'Imports' },
  'permGroup.LOYALTY': { ru: 'Лояльность', en: 'Loyalty' },
  'permGroup.ORDERS': { ru: 'Заказы', en: 'Orders' },
  'permGroup.QR': { ru: 'QR-коды', en: 'QR codes' },
  'permGroup.REPORTS': { ru: 'Отчёты', en: 'Reports' },
  'permGroup.REWARDS': { ru: 'Награды', en: 'Rewards' },
  'permGroup.SERVICES': { ru: 'Услуги', en: 'Services' },
  'permGroup.SETTINGS': { ru: 'Настройки', en: 'Settings' },
  'permGroup.STAFF': { ru: 'Сотрудники', en: 'Staff' },
  'permGroup.VEHICLES': { ru: 'Машины', en: 'Vehicles' },
  'permGroup.WALLET': { ru: 'Карты в телефоне', en: 'Wallet' },

  // ---- permanent deletion (owner only) --------------------------------------
  'delete.title': { ru: 'Удаление клиента', en: 'Delete customer' },
  'delete.intro': { ru: 'Убирает клиента и всю его историю. Отменить нельзя.', en: 'Removes the customer and their whole history. No undo.' },
  'delete.open': { ru: 'Удалить насовсем', en: 'Delete permanently' },
  'delete.confirmQuestion': { ru: 'Удалить {name} без возможности вернуть?', en: 'Delete {name} with no way back?' },
  'delete.bullet1': {
    ru: 'заказы, оплаты и возвраты этого клиента исчезнут из отчётов и выручки',
    en: 'their orders, payments and refunds leave the reports and the revenue',
  },
  'delete.bullet2': {
    ru: 'кэшбэк, баллы, награды, машины и карта в телефоне удаляются',
    en: 'cashback, points, rewards, vehicles and the wallet card are removed',
  },
  'delete.bullet3': { ru: 'останется запись в журнале', en: 'an entry stays in the audit log' },
  'delete.bullet4': {
    ru: 'отменить нельзя — ни восстановить, ни откатить',
    en: 'it cannot be undone, restored or rolled back',
  },
  'delete.typeCard': { ru: 'Впишите {card}, чтобы подтвердить', en: 'Type {card} to confirm' },
  'delete.confirm': { ru: 'Удалить насовсем', en: 'Delete for good' },

  // ---- what each setting actually does -------------------------------------
  'settingHelp.loyalty.earning_basis': { ru: 'С какой суммы считать кэшбэк и баллы: со всего заказа или только с того, что клиент заплатил деньгами.', en: 'What cashback and points are counted from: the whole order, or only what was paid in money.' },
  'settingHelp.loyalty.discount_reduces_earning': { ru: 'Включено — начисляем после скидки, выключено — с полной стоимости.', en: 'On, earning is counted after the discount; off, from the full price.' },
  'settingHelp.loyalty.allocation_policy': { ru: 'Как разносить списанный кэшбэк по строкам заказа. От этого зависит, сколько вернётся при частичном возврате.', en: 'How spent cashback is spread across order lines. It decides what comes back on a partial refund.' },
  'settingHelp.loyalty.tier_timing': { ru: 'По какому уровню считать заказ: по прежнему или по новому, если этот заказ поднял клиента.', en: 'Which tier the order earns at: the one before it, or the new one if this order moved them up.' },
  'settingHelp.loyalty.service_overrides_enabled': { ru: 'Разрешает отдельную ставку кэшбэка у конкретной услуги. Выключено — у всех общая ставка уровня.', en: 'Lets one service carry its own cashback rate. Off, every service uses the tier rate.' },
  'settingHelp.loyalty.min_order_for_cashback_tetri': { ru: 'Заказы дешевле этой суммы кэшбэк не дают. Ноль — начислять всегда.', en: 'Orders below this earn no cashback. Zero means always earn.' },
  'settingHelp.loyalty.min_order_for_points_tetri': { ru: 'То же для баллов. Ноль — начислять всегда.', en: 'The same for points. Zero means always earn.' },
  'settingHelp.loyalty.max_redemption_pct_bp': { ru: 'Какую долю заказа можно закрыть кэшбэком. 50% — половину клиент платит деньгами.', en: 'How much of an order cashback may cover. At 50% the customer pays half in money.' },
  'settingHelp.loyalty.redemption_cap_basis': { ru: 'От какой суммы считается этот процент: от всего заказа или от суммы после скидки.', en: 'What that percentage is taken from: the whole order, or the amount after discount.' },
  'settingHelp.loyalty.max_redemption_absolute_tetri': { ru: 'Потолок списания в лари, поверх процента. Пусто — ограничение только процентом.', en: 'A ceiling in lari on top of the percentage. Empty means the percentage alone decides.' },
  'settingHelp.loyalty.min_redemption_tetri': { ru: 'Меньше этой суммы списывать нельзя — чтобы не возиться с копейками.', en: 'Nothing smaller than this may be spent, so nobody fiddles with small change.' },
  'settingHelp.loyalty.allow_pending_redemption': { ru: 'Разрешить тратить кэшбэк, который ещё не созрел. Обычно выключено.', en: 'Lets cashback be spent before it matures. Normally off.' },
  'settingHelp.loyalty.pending_cashback_enabled': { ru: 'Кэшбэк становится доступен не сразу, а через срок ниже. Защита от возврата сразу после начисления.', en: 'Cashback waits before it can be spent. It guards against a refund straight after earning.' },
  'settingHelp.loyalty.pending_cashback_days': { ru: 'Сколько дней кэшбэк ждёт, прежде чем им можно расплатиться.', en: 'How many days cashback waits before it can be spent.' },
  'settingHelp.loyalty.pending_cashback_min_order_tetri': { ru: 'Созревание включается только для заказов от этой суммы. Мелкие доступны сразу.', en: 'Only orders from this amount up have to wait. Smaller ones are available at once.' },
  'settingHelp.loyalty.cashback_rounding': { ru: 'Куда округлять копейки при начислении кэшбэка. Вверх — в пользу клиента.', en: 'Which way the tetri go when cashback is worked out. Up favours the customer.' },
  'settingHelp.loyalty.points_rounding': { ru: 'То же для баллов.', en: 'The same for points.' },
  'settingHelp.loyalty.redemption_cap_rounding': { ru: 'Куда округлять посчитанный потолок списания.', en: 'Which way the calculated redemption ceiling is rounded.' },
  'settingHelp.loyalty.spend_basis': { ru: 'Что идёт в «потрачено за всё время» и двигает уровень: вся сумма заказа или только оплаченная деньгами.', en: 'What counts towards lifetime spend and moves the tier: the whole order, or only what was paid in money.' },
  'settingHelp.loyalty.blocked_customer_spend_policy': { ru: 'Засчитывать ли траты, пока клиент заблокирован.', en: 'Whether spending counts while a customer is blocked.' },
  'settingHelp.loyalty.allow_negative_cashback_balance': { ru: 'Разрешить балансу уйти в минус при возврате. Выключено — недостающее прощается.', en: 'Lets the balance go below zero on a refund. Off, the shortfall is forgiven.' },
  'settingHelp.loyalty.allow_negative_points_balance': { ru: 'То же для баллов.', en: 'The same for points.' },
  'settingHelp.loyalty.cashback_expiration_days': { ru: 'Через сколько дней без движения сгорает кэшбэк. Пусто — не сгорает.', en: 'After how many idle days cashback expires. Empty means never.' },
  'settingHelp.loyalty.points_expiration_days': { ru: 'То же для баллов. Пусто — не сгорают.', en: 'The same for points. Empty means never.' },
  'settingHelp.orders.reference_prefix': { ru: 'С чего начинается номер заказа: ORD-2026-0001.', en: 'What an order number starts with: ORD-2026-0001.' },
  'settingHelp.orders.require_vehicle': { ru: 'Нельзя провести заказ, не указав машину. Удобно, если ведёте историю по авто.', en: 'An order cannot be completed without a vehicle. Useful if you keep history per car.' },
  'settingHelp.inspection.intake_required': { ru: 'Машину не принять, пока не сфотографированы четыре стороны.', en: 'A car cannot be taken in until all four sides are photographed.' },
  'settingHelp.inspection.handover_required': { ru: 'Машину не выдать, пока не сфотографированы четыре стороны. На быструю продажу без приёмки не влияет.', en: 'A car cannot be handed over until all four sides are photographed. Walk-in sales without intake are not affected.' },
  'settingHelp.orders.discount_approval_threshold_pct_bp': { ru: 'Скидку больше этой кассир дать не может — нужен тот, у кого есть право на крупную скидку.', en: 'A cashier cannot go past this; a larger discount needs someone with the right for it.' },
  'settingHelp.orders.large_order_threshold_tetri': { ru: 'Такие заказы помечаются в списке.', en: 'Orders at or above it are flagged in the list.' },
  'settingHelp.loyalty.adjustment_manager_threshold_tetri': { ru: 'Правку баланса больше этой суммы делает только тот, у кого есть право на крупные корректировки.', en: 'An adjustment above this needs someone with the right to make large ones.' },
  'settingHelp.crm.inactive_days_warning': { ru: 'Через сколько дней без визита клиент попадает в «давно не был».', en: 'After how many days without a visit a customer is marked as away.' },
  'settingHelp.crm.high_value_lifetime_tetri': { ru: 'С какой суммы за всё время клиент считается ценным. На сводке их уход видно отдельно.', en: 'From what lifetime spend a customer counts as valuable. The dashboard flags these leaving.' },
  'settingHelp.customers.loyalty_id_prefix': { ru: 'С чего начинается номер карты клиента: AUD-2DFZ4MN.', en: 'What a customer\'s card number starts with: AUD-2DFZ4MN.' },
  'settingHelp.rewards.code_prefix': { ru: 'С чего начинается код награды, который клиент называет на кассе.', en: 'What a reward code starts with, the one a customer reads out at the till.' },
  'settingHelp.customers.access_link_ttl_minutes': { ru: 'Сколько живёт ссылка для входа, которую вы выдаёте клиенту в студии.', en: 'How long the sign-in link you hand a customer in the studio stays valid.' },
  'settingHelp.security.admin_session_idle_days': { ru: 'Пока портал открывают, вход продлевается сам.', en: 'Each use extends the sign-in.' },
  'settingHelp.security.wallet_session_ttl_minutes': { ru: 'Сколько живёт вход клиента по ссылке с карты в телефоне. Отсчёт идёт от последнего действия.', en: 'How long a sign-in from the wallet card lasts. The clock restarts with every action.' },
  'settingHelp.security.customer_session_ttl_minutes': { ru: 'Сколько клиент остаётся в кабинете после входа по коду или ссылке.', en: 'How long a customer stays signed in after a code or a link.' },
  'settingHelp.security.admin_max_failed_logins': { ru: 'Сколько раз можно ошибиться паролем, прежде чем запись заблокирует.', en: 'How many wrong passwords before the account locks.' },
  'settingHelp.security.admin_lockout_minutes': { ru: 'На сколько блокирует после этих неудач.', en: 'How long the lock lasts.' },
  'settingHelp.security.require_2fa_for_privileged_roles': { ru: 'Владелец и администратор не войдут без кода из приложения на телефоне. Остальных не касается — им код можно включить себе самим.', en: 'Owners and administrators cannot sign in without a code from an app on their phone. Other roles are unaffected and may switch it on themselves.' },
  'settingHelp.qr.portal_token_ttl_seconds': { ru: 'Сколько живёт QR в кабинете клиента, прежде чем обновится сам.', en: 'How long the QR in a customer\'s account lasts before it refreshes itself.' },
  'settingHelp.qr.resolve_rate_per_minute': { ru: 'Сколько сканирований в минуту принимаем с одного устройства.', en: 'How many scans a minute one device may send.' },
  'settingHelp.otp.code_length': { ru: 'Сколько цифр в коде из СМС.', en: 'How many digits the SMS code has.' },
  'settingHelp.otp.max_attempts': { ru: 'Сколько попыток ввести код, прежде чем он сгорит.', en: 'How many tries before the code is burned.' },
  'settingHelp.otp.max_per_ip_per_hour': { ru: 'Адрес — это устройство в сети, не почта. Защита от перебора номеров с одного компьютера.', en: 'The address is a device on the network, not an email. It stops one machine working through a list of numbers.' },
  'settingHelp.wallet.apple_pass_type_id': { ru: 'Идентификатор из аккаунта Apple Developer. Без него карты для iPhone не выпускаются.', en: 'The identifier from your Apple Developer account. Without it no iPhone cards are issued.' },
  'settingHelp.wallet.google_class_id': { ru: 'Идентификатор класса из консоли Google Wallet.', en: 'The class identifier from the Google Wallet console.' },
  'settingHelp.wallet.organization_name': { ru: 'Как называется студия на самой карте в телефоне.', en: 'What the studio is called on the card itself.' },
  'settingHelp.features.apple_wallet_enabled': { ru: 'Кнопка добавления карты в кабинете клиента.', en: 'The add-to-wallet button in the customer account.' },
  'settingHelp.features.google_wallet_enabled': { ru: 'Кнопка добавления карты в кабинете клиента.', en: 'The add-to-wallet button in the customer account.' },
  'settingHelp.features.email_enabled': { ru: 'Отправлять ли письма. Выключено — ссылку для входа сотрудник передаёт сам.', en: 'Whether email goes out. Off, staff hand the sign-in link over themselves.' },
  'settingHelp.features.referrals_enabled': { ru: 'Показывать ли клиенту его код приглашения и начислять за это бонусы.', en: 'Whether customers see their referral code and earn for it.' },
  'settingHelp.company.name': { ru: 'Как называется студия в чеках и письмах.', en: 'What the studio is called on receipts and in email.' },
  'settingHelp.company.timezone': { ru: 'По какому времени считаются дни в отчётах.', en: 'Which clock the days in reports are counted by.' },
  'settingHelp.company.default_language': { ru: 'На каком языке открывается кабинет, если язык телефона неизвестен.', en: 'Which language the account opens in when the phone\'s is unknown.' },
  'settingHelp.company.phone': { ru: 'Печатается в чеке и стоит ссылкой на карте в телефоне.', en: 'Printed on the receipt and linked from the wallet card.' },
  'settingHelp.company.support_email': { ru: 'Печатается в чеке и стоит ссылкой на карте в телефоне.', en: 'Printed on the receipt and linked from the wallet card.' },
  'settingHelp.company.address': { ru: 'Печатается в чеке.', en: 'Printed on the receipt.' },
  'setting.security.wallet_session_ttl_minutes': { ru: 'Срок входа с карты, минут', en: 'Wallet sign-in, minutes' },

  // ---- not found ------------------------------------------------------------
  'notFound.title': { ru: 'Здесь ничего нет', en: 'Nothing here' },
  'notFound.body': {
    ru: 'Такого адреса в портале сотрудников не существует.',
    en: 'That address does not match a page in the staff portal.',
  },
  'notFound.back': { ru: 'Вернуться на сводку', en: 'Back to the dashboard' },
  'inspection.intakeTitle': { ru: 'Осмотр при приёмке', en: 'Intake inspection' },
  'inspection.handoverTitle': { ru: 'Осмотр при выдаче', en: 'Handover inspection' },
  'inspection.sides': { ru: 'Со всех сторон', en: 'Every side' },
  'inspection.extras': { ru: 'Ещё по желанию', en: 'Optional extras' },
  'inspection.damages': { ru: 'Повреждения', en: 'Existing damage' },
  'inspection.addDamage': { ru: 'Отметить повреждение', en: 'Mark damage' },
  'inspection.noDamage': { ru: 'Повреждений не отмечено', en: 'No damage marked' },
  'inspection.missing': { ru: 'Осталось снять: {list}', en: 'Still to photograph: {list}' },
  'inspection.allRequired': { ru: 'Все обязательные фото сняты', en: 'Every required photo taken' },
  'inspection.records': { ru: 'VIN и пробег', en: 'VIN and mileage' },
  'inspection.fromCamera': { ru: 'Камера', en: 'Camera' },
  'inspection.fromGallery': { ru: 'Галерея', en: 'Gallery' },
  'inspection.intakeTitleLater': { ru: 'Фото при приёмке', en: 'Intake photos' },
  'inspection.intakeMissing': { ru: 'Нет фото приёмки', en: 'No intake photos' },
  'inspection.handOverWithoutPhotos': { ru: 'Выдать без фото', en: 'Hand over without photos' },
  'inspection.addIntake': { ru: 'Снять приёмку', en: 'Add intake photos' },
  'inspection.addHandover': { ru: 'Снять выдачу', en: 'Add handover photos' },
  'floor.acceptPhotosLater': { ru: 'Принять, фото позже', en: 'Take in, photos later' },
  'vin.label': { ru: 'VIN', en: 'VIN' },
  'vin.reading': { ru: 'Читаю VIN…', en: 'Reading VIN…' },
  'vin.none': { ru: 'VIN не прочитался — переснимите или впишите вручную', en: 'VIN not read — retake it or type it in' },
  'vin.failed': { ru: 'Не удалось распознать — впишите VIN вручную', en: 'Could not read — type the VIN in' },
  'vin.saved': { ru: 'VIN записан в машину', en: 'VIN saved to the car' },
  'vin.check': { ru: 'Сверьте с табличкой', en: 'Check it against the plate' },
  'vin.matches': { ru: '✓ Совпадает с карточкой', en: '✓ Matches the record' },
  'vin.differs': { ru: 'В карточке другой VIN: {vin}', en: 'The record has a different VIN: {vin}' },
  'vin.shape': { ru: '17 символов, без I, O, Q', en: '17 characters, no I, O, Q' },
  'vin.save': { ru: 'Записать VIN в машину', en: 'Save VIN to the car' },
  'vin.replace': { ru: 'Заменить в карточке', en: 'Replace on the record' },
  'vin.readAgain': { ru: 'Прочитать с фото', en: 'Read from photo' },
  'vin.noCamera': { ru: 'Нет доступа к камере', en: 'No access to the camera' },
  'vin.usePhoto': { ru: 'Снять фото', en: 'Take a photo' },
  'vin.shoot': { ru: 'Снять', en: 'Shoot' },
  'inspection.pickCarFirst': { ru: 'Сначала выберите машину — фото привязываются к ней', en: 'Pick the car first — photos are kept with it' },
  'inspection.shoot': { ru: 'Снять', en: 'Shoot' },
  'inspection.uploading': { ru: 'Загружается…', en: 'Uploading…' },
  'inspection.failed': { ru: 'Не загрузилось. Нажмите, чтобы повторить', en: 'Upload failed. Tap to retry' },
  'inspection.retake': { ru: 'Переснять', en: 'Retake' },
  'inspection.remove': { ru: 'Удалить', en: 'Delete' },
  'inspection.damageWhat': { ru: 'Что это', en: 'What it is' },
  'inspection.damageWhere': { ru: 'Где', en: 'Where' },
  'inspection.damageNote': { ru: 'Комментарий', en: 'Note' },
  'inspection.damageSave': { ru: 'Сохранить повреждение', en: 'Save damage' },
  'inspection.odometer': { ru: 'Пробег, км', en: 'Mileage, km' },
  'inspection.finish': { ru: 'Завершить осмотр', en: 'Finish inspection' },
  'inspection.finishAndHandOver': { ru: 'Завершить и выдать', en: 'Finish and hand over' },
  'inspection.handoverDone': { ru: 'Осмотр при выдаче снят', en: 'Handover photographed' },
  'inspection.intakeDone': { ru: 'Фото при приёмке', en: 'Intake photos' },
  'inspection.startHandover': { ru: 'Осмотр при выдаче', en: 'Handover inspection' },
  'inspection.by': { ru: 'снимал {name}', en: 'by {name}' },
  'inspection.photos': { ru: '{count|фото|фото|фото}', en: '{count|photo|photos}' },
  'inspection.damageCount': { ru: '{count|повреждение|повреждения|повреждений}', en: '{count|mark|marks}' },
  'inspection.openOriginal': { ru: 'Открыть оригинал', en: 'Open original' },
  'inspection.close': { ru: 'Закрыть', en: 'Close' },
  'inspection.previous': { ru: 'Предыдущее', en: 'Previous' },
  'inspection.next': { ru: 'Следующее', en: 'Next' },
  'inspection.none': { ru: 'Осмотров ещё не было', en: 'No inspections yet' },
  'inspection.orderSection': { ru: 'Осмотр машины', en: 'Vehicle inspection' },
  'inspection.notTaken': { ru: 'Фото не снимались', en: 'No photos taken' },
  'booking.duration': { ru: 'Длительность', en: 'Length' },
  'booking.master': { ru: 'Мастер', en: 'Technician' },
  'booking.minutesShort': { ru: 'мин', en: 'min' },
  'booking.hoursShort': { ru: 'ч', en: 'h' },
  'booking.daysShort': { ru: 'дн', en: 'd' },
  'booking.forMaster': { ru: 'мастер: {name}', en: 'technician: {name}' },
  'calendar.title': { ru: 'Календарь', en: 'Calendar' },
  'calendar.newBooking': { ru: 'Записать', en: 'Book' },
  'calendar.everyone': { ru: 'Все', en: 'Everyone' },
  'calendar.mine': { ru: 'Мои', en: 'Mine' },
  'calendar.legendPlan': { ru: 'запись', en: 'booking' },
  'calendar.legendFact': { ru: 'работа', en: 'work done' },
  'calendar.toFloor': { ru: 'На площадку', en: 'To the floor' },
  'calendar.openOrder': { ru: 'Открыть заказ', en: 'Open order' },
  'calendar.bookingStatus.WAITING': { ru: 'ждём', en: 'expected' },
  'calendar.bookingStatus.ARRIVED': { ru: 'приехал', en: 'arrived' },
  'calendar.bookingStatus.CANCELLED': { ru: 'отменена', en: 'cancelled' },
  'calendar.bookingStatus.NO_SHOW': { ru: 'не приехал', en: 'no-show' },
  'calendar.today': { ru: 'Сегодня', en: 'Today' },
  'calendar.day': { ru: 'День', en: 'Day' },
  'calendar.week': { ru: 'Неделя', en: 'Week' },
  'calendar.waiting': { ru: 'Ждут на площадке', en: 'Waiting on the floor' },
  'calendar.prevWeek': { ru: 'Прошлая неделя', en: 'Previous week' },
  'calendar.nextWeek': { ru: 'Следующая неделя', en: 'Next week' },
  'calendar.free': { ru: 'Свободно', en: 'Free' },
  'calendar.book': { ru: 'Записать', en: 'Book' },
  'calendar.dayEmpty': { ru: 'В этот день ничего нет', en: 'Nothing on this day' },
  'calendar.now': { ru: 'Сейчас', en: 'Now' },
  'calendar.jobTimeTitle': { ru: 'Время работы', en: 'Time on the job' },
  'calendar.jobTimeEdit': { ru: 'Изменить время', en: 'Change time' },
  'calendar.jobTimeManual': { ru: 'Время задано вручную', en: 'Time set by hand' },
  'calendar.jobTimeSave': { ru: 'Сохранить время', en: 'Save time' },
  'calendar.jobTimeReset': { ru: 'Вернуть по факту работы', en: 'Back to the recorded time' },
  'calendar.jobStart': { ru: 'Начало', en: 'Start' },
  'calendar.jobEnd': { ru: 'Конец', en: 'End' },
  'calendar.jobLength': { ru: 'Длительность', en: 'Length' },
  'calendar.jobTimeInvalid': { ru: 'Укажите начало и конец', en: 'Set a start and an end' },
  'calendar.jobTimeEndFirst': { ru: 'Конец должен быть позже начала', en: 'The end must come after the start' },
  'calendar.jobTimeTooLong': { ru: 'Не больше месяца', en: 'A month at most' },
  'nav.calendar': { ru: 'Календарь', en: 'Calendar' },
  'vehicle.history': { ru: 'История машины', en: 'Vehicle history' },
  'vehicle.owner': { ru: 'Владелец', en: 'Owner' },
  'vehicle.orders': { ru: 'Заказы', en: 'Orders' },
  'vehicle.inspections': { ru: 'Осмотры', en: 'Inspections' },
  'vehicle.timeline': { ru: 'Что было с машиной', en: 'What happened to this car' },
  'vehicle.notFound': { ru: 'Машина не найдена', en: 'Vehicle not found' },
  'vehicle.archived': { ru: 'В архиве', en: 'Archived' },
  'inspection.slot.FRONT': { ru: 'Спереди', en: 'Front' },
  'inspection.slot.REAR': { ru: 'Сзади', en: 'Rear' },
  'inspection.slot.LEFT': { ru: 'Слева', en: 'Left' },
  'inspection.slot.RIGHT': { ru: 'Справа', en: 'Right' },
  'inspection.slot.INTERIOR': { ru: 'Салон', en: 'Interior' },
  'inspection.slot.DASHBOARD': { ru: 'Приборка и пробег', en: 'Dashboard and mileage' },
  'inspection.slot.VIN': { ru: 'VIN-код', en: 'VIN' },
  'inspection.slot.DAMAGE': { ru: 'Повреждение', en: 'Damage' },
  'inspection.damage.SCRATCH': { ru: 'Царапина', en: 'Scratch' },
  'inspection.damage.CHIP': { ru: 'Скол', en: 'Chip' },
  'inspection.damage.DENT': { ru: 'Вмятина', en: 'Dent' },
  'inspection.damage.CRACK': { ru: 'Трещина', en: 'Crack' },
  'inspection.damage.RUST': { ru: 'Ржавчина', en: 'Rust' },
  'inspection.damage.OTHER': { ru: 'Другое', en: 'Other' },
  'inspection.part.BUMPER_FRONT': { ru: 'Бампер передний', en: 'Front bumper' },
  'inspection.part.BUMPER_REAR': { ru: 'Бампер задний', en: 'Rear bumper' },
  'inspection.part.HOOD': { ru: 'Капот', en: 'Hood' },
  'inspection.part.ROOF': { ru: 'Крыша', en: 'Roof' },
  'inspection.part.TRUNK': { ru: 'Багажник', en: 'Trunk' },
  'inspection.part.FENDER_FRONT_LEFT': { ru: 'Крыло переднее левое', en: 'Front left fender' },
  'inspection.part.FENDER_FRONT_RIGHT': { ru: 'Крыло переднее правое', en: 'Front right fender' },
  'inspection.part.FENDER_REAR_LEFT': { ru: 'Крыло заднее левое', en: 'Rear left fender' },
  'inspection.part.FENDER_REAR_RIGHT': { ru: 'Крыло заднее правое', en: 'Rear right fender' },
  'inspection.part.DOOR_FRONT_LEFT': { ru: 'Дверь передняя левая', en: 'Front left door' },
  'inspection.part.DOOR_FRONT_RIGHT': { ru: 'Дверь передняя правая', en: 'Front right door' },
  'inspection.part.DOOR_REAR_LEFT': { ru: 'Дверь задняя левая', en: 'Rear left door' },
  'inspection.part.DOOR_REAR_RIGHT': { ru: 'Дверь задняя правая', en: 'Rear right door' },
  'inspection.part.SILL_LEFT': { ru: 'Порог левый', en: 'Left sill' },
  'inspection.part.SILL_RIGHT': { ru: 'Порог правый', en: 'Right sill' },
  'inspection.part.MIRROR_LEFT': { ru: 'Зеркало левое', en: 'Left mirror' },
  'inspection.part.MIRROR_RIGHT': { ru: 'Зеркало правое', en: 'Right mirror' },
  'inspection.part.WINDSHIELD': { ru: 'Лобовое стекло', en: 'Windshield' },
  'inspection.part.REAR_WINDOW': { ru: 'Заднее стекло', en: 'Rear window' },
  'inspection.part.SIDE_WINDOW': { ru: 'Боковое стекло', en: 'Side window' },
  'inspection.part.HEADLIGHT': { ru: 'Фара', en: 'Headlight' },
  'inspection.part.TAILLIGHT': { ru: 'Задний фонарь', en: 'Tail light' },
  'inspection.part.WHEEL': { ru: 'Колесо или диск', en: 'Wheel or rim' },
  'inspection.part.INTERIOR': { ru: 'Салон', en: 'Interior' },
  'inspection.part.OTHER': { ru: 'Другое место', en: 'Elsewhere' },
} as const;

export type StringKey = keyof typeof STRINGS;
